'use client';
import { getSessionId } from '@/lib/trackerSession';
import { fx } from '@/lib/funnelTrack'; // 14/10/26 funnel events
// @ts-ignore — the ONE client tracking door (plain JS, shared with the 3D journey)
import { questionnaireLog } from '@/lib/trackClient.mjs';
import { CATEGORY_SLUG_REDIRECTS } from '@/lib/categorySlugRedirects';
import { proServesCity, proDistanceKm, cityAnchor } from '@/lib/proMatching';
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { SMART_INTAKE_KEY } from './SmartIntakeBar';
import type { SmartIntakeContext } from './SmartIntakeBar';
import { cleanCity } from '@/lib/city';
import { categoryService } from '@/lib/waMessage';
import { popularCities, findCity, haversineKm, citySlug, DEFAULT_RADIUS_KM } from '@/lib/israelCities';
import { suggestCities } from '@/lib/searchText'; // typos, keyboard-layout slips, English (2026-09-28)
import { resetCity, useSmartIntakeCtx, notifySmartIntake, readSmartIntakeNow, resolvePageCity, notePagePick } from '@/lib/smartIntakeStore';
import CityQuestion from './CityQuestion';

// ── STANDING RULE (2026-08-24, explicit user directive — see
// docs/QUESTIONNAIRE_CHANGELOG.md): every questionnaire, on every possible answer path,
// must show AT MOST 3 total questions INCLUDING the always-added city question — i.e. at
// most 2 questions in a category's `he_questionnaires.questions_json`, even across
// showIf branches (a branch question counts against this same budget, not on top of it).
// More questions = more drop-off before the lead reaches the pro. This isn't enforced at
// the type level (questions_json is hand-authored SQL, no admin UI) — when authoring or
// editing ANY questionnaire, count every branch's longest path by hand, or run
// `scripts/questionnaire-question-count.mjs`'s maxPathCount() logic, and keep it ≤3.
// `he_questionnaires.question_count` stores this per-row for auditing across categories.
export const MAX_QUESTIONS_PER_FLOW = 3;

export interface QOption { value: string; label: string; emoji: string; }
export interface Question {
  id: string; question: string; outputKey: string;
  options: QOption[]; allowOther: boolean; skipAllowed: boolean;
  // Only shown if a PRIOR question's answer (by outputKey) has one of these values —
  // e.g. אינסטלטורים's install/problem follow-up questions. Must reference a question
  // earlier in the authored order (city is always first, never a valid showIf target).
  // Omitted entirely by every flat, non-branching questionnaire — fully backward compatible.
  showIf?: { outputKey: string; values: string[] };
}
export interface Questionnaire {
  categorySlug: string; version: string; questions: Question[];
  // 2026-07-29 (admin questionnaire-stats): set for article-seeded questionnaires so
  // each article's own mini-questionnaire gets a distinct identity in he_questionnaire_logs
  // instead of colliding with every other article in the same category under one id.
  articleId?: number;
  // Service-cluster questionnaire (he_service_clusters, e.g. "עינית דיגיטלית" inside
  // מנעולנים) — its outputKeys are cluster-specific, so WA-message building must use
  // the generic clause joiner, NOT the category's CATEGORY_BUILDERS entry (which
  // expects the category questionnaire's own outputKeys and would return '').
  serviceCluster?: boolean;
  // Service cluster's display name (e.g. "עינית דיגיטלית") — prepended to the WA
  // clause so answers like "כבר נרכשה" keep their object: "עינית דיגיטלית, כבר נרכשה".
  serviceName?: string;
}

interface Answer { questionId: string; outputKey: string; value: string | null; label: string | null; freeText: string | null; }

function matchesShowIf(showIf: NonNullable<Question['showIf']>, answerMap: Record<string, Answer>): boolean {
  const ans = Object.values(answerMap).find(a => a.outputKey === showIf.outputKey);
  return !!ans && !!ans.value && showIf.values.includes(ans.value);
}

// ── Nearby cities map (Claude-generated from knowledge) ──────────────────────
const NEARBY: Record<string, string[]> = {
  'תל אביב': ['גבעתיים', 'רמת גן', 'בני ברק', 'חולון', 'בת ים', 'הרצליה', 'רמת השרון'],
  'יפו': ['תל אביב', 'בת ים', 'חולון', 'ראשון לציון'],
  'ירושלים': ['בית שמש', 'מבשרת ציון', 'גבעת זאב', 'מעלה אדומים', 'כפר אדומים'],
  'חיפה': ['קריית ים', 'קריית אתא', 'קריית ביאליק', 'קריית מוצקין', 'נשר', 'עכו'],
  'ראשון לציון': ['נס ציונה', 'רחובות', 'חולון', 'בת ים', 'לוד', 'יבנה'],
  'פתח תקווה': ['ראש העין', 'כפר סבא', 'הוד השרון', 'בני ברק', 'גבעת שמואל', 'רמת גן'],
  'אשדוד': ['אשקלון', 'קריית גת', 'גן יבנה', 'יבנה', 'נס ציונה'],
  'באר שבע': ['נתיבות', 'דימונה', 'ערד', 'אשקלון', 'קריית גת'],
  'נתניה': ['כפר יונה', 'חדרה', 'הרצליה', 'רעננה', 'כפר סבא', 'עמק חפר'],
  'בני ברק': ['תל אביב', 'רמת גן', 'גבעתיים', 'פתח תקווה'],
  'הרצליה': ['תל אביב', 'רמת השרון', 'רעננה', 'כפר סבא', 'נתניה'],
  'רמת גן': ['תל אביב', 'גבעתיים', 'בני ברק', 'פתח תקווה'],
  'גבעתיים': ['תל אביב', 'רמת גן', 'בני ברק'],
  'בת ים': ['חולון', 'תל אביב', 'ראשון לציון'],
  'חולון': ['בת ים', 'תל אביב', 'ראשון לציון'],
  'כפר סבא': ['רעננה', 'הוד השרון', 'פתח תקווה', 'נתניה'],
  'רחובות': ['נס ציונה', 'יבנה', 'ראשון לציון', 'רמלה', 'לוד'],
  'אשקלון': ['אשדוד', 'נתיבות', 'קריית גת', 'שדרות'],
  'מודיעין': ['שוהם', 'לוד', 'רמלה', 'ירושלים', 'ראשון לציון'],
  'רמת השרון': ['תל אביב', 'הרצליה', 'רעננה', 'כפר סבא'],
  'רעננה': ['כפר סבא', 'הרצליה', 'הוד השרון', 'פתח תקווה'],
  'לוד': ['רמלה', 'ראשון לציון', 'מודיעין', 'פתח תקווה'],
  'רמלה': ['לוד', 'ראשון לציון', 'נס ציונה', 'מודיעין'],
  'נס ציונה': ['ראשון לציון', 'רחובות', 'יבנה', 'אשדוד'],
  'יבנה': ['אשדוד', 'נס ציונה', 'רחובות', 'ראשון לציון'],
  'עכו': ['נהריה', 'חיפה', 'קריית אתא', 'קריית ביאליק'],
  'נהריה': ['עכו', 'חיפה', 'מעלות-תרשיחא'],
  'קריית אתא': ['חיפה', 'קריית ביאליק', 'קריית מוצקין', 'קריית ים'],
  'קריית גת': ['אשדוד', 'אשקלון', 'באר שבע', 'שדרות'],
  'הוד השרון': ['כפר סבא', 'רעננה', 'הרצליה', 'פתח תקווה'],
  'גבעת שמואל': ['פתח תקווה', 'רמת גן', 'כפר סבא'],
  'ראש העין': ['פתח תקווה', 'כפר סבא', 'הוד השרון'],
  'נתיבות': ['אשקלון', 'באר שבע', 'שדרות'],
  'כפר יונה': ['נתניה', 'חדרה', 'קיסריה'],
  'קריית שמונה': ['מטולה', 'קצרין', 'צפת'],
  'צפת': ['קריית שמונה', 'טבריה', 'מגדל'],
  'טבריה': ['נצרת', 'עפולה', 'צפת'],
  'נצרת': ['עפולה', 'יוקנעם', 'טבריה', 'נצרת עילית'],
  'כפר כנא': ['נצרת', 'עפולה'],
  'חדרה': ['נתניה', 'כפר יונה', 'קיסריה', 'זכרון יעקב'],
  'נשר': ['חיפה', 'קריית אתא', 'טירת כרמל'],
};

// ── STANDING RULE (2026-08-24, explicit user directive): the city question — always the
// FIRST question of every questionnaire — must read as a personal address to the visitor
// using the category's own PROFESSIONAL TITLE (he_categories.name_he, e.g. "טכנאי
// מזגנים", "שמאי מקרקעין"), not the generic service-action noun (categoryService(),
// "טיפול והתקנת מזגנים") used elsewhere (WA message body, etc — unchanged, deliberately
// different wording for a different purpose). Was "באיזו עיר צריך {service}?" (generic/
// transactional), then "באיזו עיר ניתן למצוא לך {title}?" (same day — user asked to
// drop "ניתן" as unnecessary), briefly "באיזה עיר למצוא לך {title}?" (user's own typing
// that round used "באיזה"), corrected back to the grammatically standard "באיזו" (user
// caught it themselves right after) — final: "באיזו עיר למצוא לך {professional
// title}?" (personal, addresses the visitor directly) — e.g. "באיזו עיר למצוא לך טכנאי
// מזגנים?".
function buildCityQuestion(proCity: string | null | undefined, categoryTitle: string): Question {
  const clean = cleanCity(proCity);
  let cities: string[];
  if (clean && NEARBY[clean]) {
    cities = [clean, ...NEARBY[clean]].slice(0, 8);
  } else if (clean) {
    cities = Array.from(new Set([clean, ...popularCities(8)])).slice(0, 8);
  } else {
    cities = popularCities(8); // category page → most populous cities
  }
  const proNoun = categoryTitle ? categoryTitle : 'איש מקצוע';
  return {
    id: 'q_city',
    question: `באיזו עיר למצוא לך ${proNoun}?`,
    outputKey: 'city',
    skipAllowed: false, // city is mandatory — drives the location filtering
    allowOther: true,
    options: cities.map(c => ({ value: c, label: c, emoji: '' })),
  };
}

// bughunter 2026-09-30: every non-city question renders the wizard's OWN free-text "✏️ אחר" chip, so a question
// option that is itself an "other" catch-all (אחר / משהו אחר / "בנק אחר" / "סיבה אחרת") shows a duplicate. Mirror of
// scripts/kwlab/quizGate.mjs isOtherOption() (which refuses such quizzes at generation) — keep the two in sync.
const OTHER_WORD_RE = /^(?:אחר|אחרת|אחרים|אחרות|other)$/;
function isOtherCatchAll(label: string | undefined | null): boolean {
  const w = String(label || '').replace(/[\u0591-\u05C7]/g, '').replace(/[^\u05D0-\u05EAa-zA-Z\s]/g, ' ')
    .trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!w.length || w.length > 4) return false;
  return OTHER_WORD_RE.test(w[w.length - 1]) || OTHER_WORD_RE.test(w[0]);
}
/** Options to render next to the auto "✏️ אחר" chip — drops "other" catch-alls, but never below 2 real options. */
function visibleOptions<T extends { label?: string }>(opts: T[] | undefined | null): T[] {
  const all = opts || [];
  const kept = all.filter(o => !isOtherCatchAll(o.label));
  return kept.length >= 2 ? kept : all;
}

function genSession(): string { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

function answerText(a: Answer): string { return (a.freeText || a.label || '').trim(); }

// ── שמאי-מקרקעין v2.0 (2026-08-24, max-3-questions rewrite — see
// docs/QUESTIONNAIRE_CHANGELOG.md): city → assetType → ONE size/units follow-up branched
// by assetType (size in מ״ר for apartments, דונם for land, unit-count for a whole
// building). purpose/formalType/bank were cut entirely (never load-bearing for a lead —
// the pro asks those directly) to cut friction. Glue assetType+size/units into one
// clause, no commas, since together they describe a single thing being appraised — e.g.
// "דירה בבניין בגודל 80–120 מ״ר" / "קרקע חקלאית בגודל 1–5 דונם".
function buildRealEstateNeed(byKey: Record<string, Answer>): string {
  let primary = '';
  if (byKey.assetType) primary = answerText(byKey.assetType);
  if (byKey.size) {
    let t = answerText(byKey.size);
    if (!/מ[״"׳]?ר|מטר|דונם/.test(t)) t += ' מ״ר'; // keep units explicit if not already present
    primary = primary ? `${primary} בגודל ${t}` : `בגודל ${t}`;
  }
  if (byKey.units) {
    const t = answerText(byKey.units);
    primary = primary ? `${primary}, ${t}` : t;
  }
  return primary;
}

// ── אינסטלטורים branches into installType (new installation) or problemType (existing
// issue) via showIf — whichever one is present IS the whole "what's needed" clause on its
// own (e.g. "נזילת מים", "התקנת ברז"), no gluing required. location/propertyType/insurance
// are shared across both branches. טכנאי-מזגנים deliberately reuses these exact outputKeys
// (see CATEGORY_BUILDERS below), so this one function serves both categories.
function buildPlumbingNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const core = byKey.installType || byKey.problemType;
  if (core) parts.push(answerText(core));
  if (byKey.location) parts.push(`ב${answerText(byKey.location)}`);
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  if (byKey.insurance) {
    if (byKey.insurance.value === 'yes') parts.push('לצורך תביעת ביטוח');
    else if (byKey.insurance.value !== 'no') parts.push(answerText(byKey.insurance));
  }
  return parts.join(', ');
}

// ── All 8 lawyer categories (עורכי-דין + its 7 practice-area sub-categories) share the
// SAME outputKeys (matter/stage/urgency/documents) by design. The generic fallback would
// comma-join stage+urgency+documents metadata alongside the actual legal matter — e.g.
// "גירושין, יש הליך פתוח כבר, חלקית" — which reads as a jumbled fact-list, not a clause
// (urgency/documents describe the LEAD, not the "need"). Keep the message to the matter
// itself, only appending stage when it adds real signal (an ongoing case or a ruling
// already in hand); urgency/documents stay logged per-answer for internal use only,
// exactly like urgency already is in the generic path below.
function buildLawyerNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.matter) parts.push(answerText(byKey.matter));
  if (byKey.stage) {
    const v = byKey.stage.value;
    if (v === 'ongoing') parts.push('יש כבר הליך פתוח');
    else if (v === 'post_ruling') parts.push('יש כבר פסק דין/החלטה בנושא');
    else parts.push(answerText(byKey.stage));
  }
  return parts.join(', ');
}

// ── חשמלאים: property_type/need/cert_purpose(showIf need=inspection)/scope/urgency.
function buildElectricianNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.need) parts.push(answerText(byKey.need));
  if (byKey.cert_purpose) parts.push(`לצורך ${answerText(byKey.cert_purpose)}`);
  if (byKey.property_type) parts.push(`ב${answerText(byKey.property_type)}`);
  return parts.join(', ');
}

// ── מוסכים: requestType branches into breakdownSymptom/issueNature/routineType/accidentType
// (one of these is the real "what happened" clause); towingNeeded/insurance are appended as
// natural asides only when they add something (yes/no, not the "not_sure" filler value).
function buildGarageNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const detail = byKey.breakdownSymptom || byKey.issueNature || byKey.routineType || byKey.accidentType;
  if (detail) parts.push(answerText(detail));
  else if (byKey.requestType) parts.push(answerText(byKey.requestType));
  if (byKey.vehicleType) parts.push(`ב${answerText(byKey.vehicleType)}`);
  if (byKey.towingNeeded?.value === 'towing_yes') parts.push('וצריך גרר');
  else if (byKey.towingNeeded && !['towing_no', 'not_sure'].includes(byKey.towingNeeded.value || '')) parts.push(answerText(byKey.towingNeeded));
  if (byKey.insurance?.value === 'yes') parts.push('דרך ביטוח');
  else if (byKey.insurance?.value === 'no') parts.push('בתשלום פרטי');
  else if (byKey.insurance && byKey.insurance.value !== 'not_sure') parts.push(answerText(byKey.insurance));
  return parts.join(', ');
}

// ── קבלני-שיפוצים: scope (full/room/small) branches into totally different follow-up
// fields — glue each branch into its own natural clause (mirrors buildRealEstateNeed).
function buildRenovationNeed(byKey: Record<string, Answer>): string {
  const scope = byKey.scope?.value;
  const propText = byKey.propertyType ? answerText(byKey.propertyType) : '';
  if (scope === 'full') {
    let s = 'שיפוץ כללי' + (propText ? ` של ${propText}` : '');
    if (byKey.size) s += ` בגודל ${answerText(byKey.size)}`;
    if (byKey.planStatus?.value === 'needs_design') s += ', כולל עיצוב/תכנון';
    else if (byKey.planStatus?.value === 'has_plan') s += ', יש כבר תוכנית מוכנה';
    else if (byKey.planStatus && byKey.planStatus.value !== 'unsure') s += `, ${answerText(byKey.planStatus)}`;
    return s;
  }
  if (scope === 'room' && byKey.roomType) {
    return `שיפוץ ה${answerText(byKey.roomType)}` + (propText ? ` ב${propText}` : '');
  }
  if (scope === 'small' && byKey.smallJobType) {
    return answerText(byKey.smallJobType) + (propText ? ` ב${propText}` : '');
  }
  // Typed a custom ("אחר") scope — never silently drop the primary clause the user wrote.
  if (byKey.scope) return answerText(byKey.scope) + (propText ? ` ב${propText}` : '');
  return propText || '';
}

// ── רואי-חשבון: client_type branches into private_need or business_service; frequency and
// employees are appended as natural asides (skipped when they add nothing, e.g. no employees).
function buildAccountantNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const detail = byKey.private_need || byKey.business_service;
  if (detail) parts.push(answerText(detail));
  else if (byKey.client_type) parts.push(answerText(byKey.client_type));
  if (byKey.frequency?.value === 'monthly') parts.push('כשירות חודשי קבוע');
  else if (byKey.frequency?.value === 'one_time') parts.push('כטיפול חד פעמי');
  else if (byKey.frequency && byKey.frequency.value !== 'not_sure') parts.push(answerText(byKey.frequency));
  if (byKey.employees && byKey.employees.value !== 'none') parts.push(`עם ${answerText(byKey.employees)}`);
  return parts.join(', ');
}

// ── יועצי-מס: several option labels contain their own " – " (e.g. "טרם פתחתי עסק – רוצה
// להתחיל"), which would collide visually with the WA template's own " – cleanNeed." dash.
// Map the known option values to dash-free phrases; unrecognized/"other" free text still
// falls back to the raw label so nothing is silently dropped.
const TAX_MATTER_PHRASE: Record<string, string> = {
  annual_report: 'הגשת דוח שנתי',
  business_setup: 'ליווי בפתיחת עסק',
  ongoing_vat_income: 'ניהול שוטף של מע"מ ומקדמות מס הכנסה',
  company_yearend: 'הנהלת חשבונות וסגירת שנה לחברה',
  audit_debt_rep: 'ייצוג מול רשות המסים בביקורת או בחוב',
  tax_planning: 'תכנון מס וחיסכון',
};
const TAX_BUSINESS_STATUS_PHRASE: Record<string, string> = {
  self_employed: 'כעצמאי/עוסק',
  company: 'עבור חברה בע"מ',
  new_business: 'לקראת פתיחת עסק חדש',
  employee_extra_income: 'כשכיר עם הכנסה נוספת',
};
function buildTaxAdvisorNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const matter = byKey.taxMatter;
  if (matter) parts.push((matter.value && TAX_MATTER_PHRASE[matter.value]) || answerText(matter));
  if (byKey.businessStatus) {
    const phrase = byKey.businessStatus.value ? TAX_BUSINESS_STATUS_PHRASE[byKey.businessStatus.value] : undefined;
    parts.push(phrase || answerText(byKey.businessStatus));
  }
  if (byKey.auditStage && matter?.value === 'audit_debt_rep') parts.push(`כרגע ${answerText(byKey.auditStage)}`);
  return parts.join(', ');
}

// ── נגרים: requestType branches into newItemType (+ optional material) or repairType.
function buildCarpenterNeed(byKey: Record<string, Answer>): string {
  const isNew = byKey.requestType?.value === 'new';
  const item = isNew ? byKey.newItemType : byKey.repairType;
  if (!item) return byKey.requestType ? answerText(byKey.requestType) : '';
  const itemText = answerText(item);
  if (isNew) {
    if (byKey.material && byKey.material.value !== 'no_preference') {
      return `${itemText}, חומר מועדף ${answerText(byKey.material)}`;
    }
    return itemText;
  }
  return `תיקון: ${itemText}`;
}

// ── מנעולנים: situation is a 5-way fork (locked_out/lock_broken/replace_upgrade/
// new_install/key_copy), each needing its own clause shape.
function buildLocksmithNeed(byKey: Record<string, Answer>): string {
  const situation = byKey.situation?.value;
  if (situation === 'locked_out') {
    const door = byKey.door_type ? answerText(byKey.door_type) : null;
    return door ? `ננעלתי בחוץ (${door})` : 'ננעלתי בחוץ';
  }
  if (situation === 'key_copy') {
    const count = byKey.key_count ? answerText(byKey.key_count) : null;
    return count ? `שכפול מפתחות (${count})` : 'שכפול מפתחות';
  }
  const parts: string[] = [];
  if (situation === 'lock_broken') parts.push('מנעול תקוע/שבור');
  else if (situation === 'replace_upgrade') parts.push('החלפת מנעול/שדרוג אבטחה');
  else if (situation === 'new_install') parts.push('התקנת מנעול חדש');
  else if (byKey.situation) parts.push(answerText(byKey.situation));
  if (byKey.lock_type && byKey.lock_type.value !== 'unsure') parts.push(answerText(byKey.lock_type));
  if (byKey.door_material) parts.push(`בדלת ${answerText(byKey.door_material)}`);
  if (byKey.property_type) parts.push(answerText(byKey.property_type));
  return parts.join(', ');
}

// ── דיאטניות: flat questionnaire (goal/age_group/format/process), glued into one clause.
function buildDietitianNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.goal) parts.push(`בנושא ${answerText(byKey.goal)}`);
  if (byKey.age_group && byKey.age_group.value !== 'self') parts.push(answerText(byKey.age_group));
  if (byKey.format && byKey.format.value !== 'flexible') parts.push(`במפגשים ${answerText(byKey.format)}`);
  if (byKey.process && byKey.process.value !== 'unsure') parts.push(`במסגרת ${answerText(byKey.process)}`);
  return parts.join(', ');
}

// ── מעצבי-פנים: scope+phase glued as primary clause, then style/area/budget as asides.
function buildInteriorDesignNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  let primary = byKey.scope ? answerText(byKey.scope) : '';
  if (byKey.phase) primary = primary ? `${primary} (${answerText(byKey.phase)})` : answerText(byKey.phase);
  if (primary) parts.push(primary);
  if (byKey.style && byKey.style.value !== 'no_preference') parts.push(`בסגנון ${answerText(byKey.style)}`);
  if (byKey.area) {
    let t = answerText(byKey.area);
    if (!/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
    parts.push(`בשטח ${t}`);
  }
  if (byKey.budget && byKey.budget.value !== 'flexible') parts.push(`בתקציב של כ-${answerText(byKey.budget)}`);
  return parts.join(', ');
}

// ── גננים: requestType branches into maintenance (recurring, with size+frequency) or a
// one-time project (projectType).
function buildGardenerNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const isMaintenance = byKey.requestType?.value === 'maintenance';
  if (isMaintenance) {
    let s = 'תחזוקת גינה שוטפת';
    if (byKey.gardenSize) {
      let t = answerText(byKey.gardenSize);
      if (!/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
      s += ` בגודל ${t}`;
    }
    parts.push(s);
    if (byKey.frequency) parts.push(answerText(byKey.frequency));
  } else if (byKey.projectType) {
    parts.push(answerText(byKey.projectType));
  } else if (byKey.requestType) {
    parts.push(answerText(byKey.requestType));
  }
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  return parts.join(', ');
}

// ── וטרינרים: reasonType branches into routineDetail or symptomDetail (owner-observed,
// not a diagnosis); animalType is folded into the clause rather than comma-joined.
function buildVetNeed(byKey: Record<string, Answer>): string {
  const animal = byKey.animalType ? answerText(byKey.animalType) : '';
  let primary = '';
  if (byKey.symptomDetail) {
    const t = answerText(byKey.symptomDetail);
    primary = animal ? `ל${animal} עם ${t}` : `בגלל ${t}`; // gender law 01/10: no slash (שסובל/ת)
  } else if (byKey.routineDetail) {
    const t = answerText(byKey.routineDetail);
    primary = animal ? `ל${animal} לצורך ${t}` : `לצורך ${t}`;
  } else if (byKey.reasonType) {
    const t = answerText(byKey.reasonType);
    primary = animal ? `${animal}, ${t}` : t;
  } else if (animal) {
    primary = animal;
  }
  const parts = [primary].filter(Boolean);
  if (byKey.serviceType?.value === 'home_visit') parts.push('בביקור בית');
  return parts.join(', ');
}

// ── טכנאי-מחשבים: requestType branches into homeProblem/businessNeed/purchaseUse/setupType.
function buildComputerTechNeed(byKey: Record<string, Answer>): string {
  if (byKey.purchaseUse) return `ייעוץ לרכישת מחשב ל${answerText(byKey.purchaseUse)}`;
  let primary = '';
  const detail = byKey.homeProblem || byKey.businessNeed || byKey.setupType;
  if (detail) primary = answerText(detail);
  else if (byKey.requestType) primary = answerText(byKey.requestType);
  if (byKey.deviceType) primary = primary ? `${primary} (${answerText(byKey.deviceType)})` : answerText(byKey.deviceType);
  return primary;
}

// ── יועצי-משכנתאות v2.0 (2026-08-24, max-3-questions rewrite — see
// docs/QUESTIONNAIRE_CHANGELOG.md): city → mortgageType → propertyPrice. purpose/
// propertyStatus/currentBank/timeline/loanAmount/equity were cut entirely (6 questions
// worst-case including city → 3) — none of them are needed to route the lead, the
// advisor asks the rest directly on the call. mortgageType=unsure gets its own opening
// clause rather than being silently dropped.
function buildMortgageAdvisorNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.mortgageType?.value === 'unsure') parts.push('ייעוץ משכנתא, עדיין לא ברור איזה סוג');
  else if (byKey.mortgageType) parts.push(answerText(byKey.mortgageType));
  if (byKey.propertyPrice) parts.push(`לנכס בשווי ${answerText(byKey.propertyPrice)}`);
  return parts.join(', ');
}

// ── רופאי-שיניים: flat (reason/patientType/coverage); urgency excluded like everywhere else.
function buildDentistNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.reason) parts.push(answerText(byKey.reason));
  if (byKey.patientType && byKey.patientType.value !== 'self') parts.push(`עבור ${answerText(byKey.patientType)}`);
  if (byKey.coverage) {
    const v = byKey.coverage.value;
    if (v === 'private_pay') parts.push('בתשלום פרטי');
    else if (v === 'private_insurance') parts.push('עם ביטוח שיניים פרטי');
    else if (v === 'health_fund') parts.push('דרך קופת החולים');
    else if (v !== 'unsure') parts.push(answerText(byKey.coverage));
  }
  return parts.join(', ');
}

// ── מאמני-כושר: flat (goal/format/commitment/experience/frequency), glued into one clause.
function buildFitnessTrainerNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.goal) parts.push(`בנושא ${answerText(byKey.goal)}`);
  if (byKey.format) parts.push(answerText(byKey.format));
  if (byKey.commitment && byKey.commitment.value !== 'unsure') parts.push(`במסגרת ${answerText(byKey.commitment)}`);
  if (byKey.experience) parts.push(answerText(byKey.experience));
  if (byKey.frequency && byKey.frequency.value !== 'flexible') parts.push(answerText(byKey.frequency));
  return parts.join(', ');
}

// ── צלמים: flat (eventType/duration/groupSize/deliverables/budget). "urgency" here is
// literally the event date, deliberately excluded like every other category's urgency.
function buildPhotographerNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.eventType) parts.push(answerText(byKey.eventType));
  if (byKey.groupSize) {
    const v = byKey.groupSize.value;
    const t = answerText(byKey.groupSize);
    parts.push((v === 'medium_event' || v === 'large_event') ? `עם ${t}` : `עבור ${t}`);
  }
  if (byKey.duration && byKey.duration.value !== 'unsure') parts.push(`לצילום של ${answerText(byKey.duration)}`);
  if (byKey.deliverables) parts.push(`כולל ${answerText(byKey.deliverables)}`);
  if (byKey.budget && byKey.budget.value !== 'flexible') parts.push(`בתקציב של ${answerText(byKey.budget)}`);
  return parts.join(', ');
}

// ── קבלני-איטום: requestType branches into an active leak (leakSource+damageExtent) or a
// planned/preventive project (projectType) — mirrors the plumbing install/problem pattern.
// v3.0 (Founder 2026-09-27) for BOTH קבלן-איטום and גגן: city → roofSize (מ״ר ranges / unknown) → propertyType
// (בית קרקע / בניין). "לא ידוע" is a non-fact and never enters the message. Reads "איטום גג 50-100 מ״ר בבניין".
function buildRoofV3Need(byKey: Record<string, Answer>, lead: string): string | null {
  if (!byKey.roofSize && !byKey.propertyType) return null;
  const parts: string[] = [lead];
  if (byKey.roofSize && byKey.roofSize.value !== 'unknown') {
    let t = answerText(byKey.roofSize);
    if (t && !/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
    if (t) parts.push(t);
  }
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  return parts.join(' ');
}

function buildWaterproofingNeed(byKey: Record<string, Answer>): string {
  const v3 = buildRoofV3Need(byKey, 'איטום גג'); if (v3) return v3;
  const parts: string[] = [];
  const isLeak = byKey.requestType?.value === 'leak';
  if (isLeak) {
    const source = byKey.leakSource ? answerText(byKey.leakSource) : '';
    parts.push(source ? `נזילה פעילה ב${source}` : 'נזילה פעילה');
    if (byKey.damageExtent && byKey.damageExtent.value !== 'none') parts.push(`עם ${answerText(byKey.damageExtent)}`);
  } else if (byKey.projectType) {
    parts.push(answerText(byKey.projectType));
  } else if (byKey.requestType) {
    parts.push(answerText(byKey.requestType));
  }
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  if (byKey.size && byKey.size.value !== 'not_sure') {
    let t = answerText(byKey.size);
    if (!/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
    parts.push(t);
  }
  if (byKey.urgency?.value === 'now' && !isLeak) parts.push('באופן דחוף');
  else if (byKey.urgency?.value === 'season' && byKey.projectType?.value !== 'seasonal') parts.push('לפני עונת הגשמים');
  return parts.join(', ');
}

// ── מדבירים: requestType branches into contract (recurring, for a business+businessType) or
// one_time/not_sure (a specific pest issue, with propertyType + whether it's recurring).
function buildPestControlNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const isContract = byKey.requestType?.value === 'contract';
  const pestAns = byKey.pestType;
  const pestKnown = !!(pestAns && pestAns.value !== 'general_unsure' && answerText(pestAns));
  const pestText = pestKnown ? answerText(pestAns!) : '';
  if (isContract) {
    let s = pestText ? `הדברה קבועה ל${pestText}` : 'הדברה קבועה';
    if (byKey.businessType) s += ` ב${answerText(byKey.businessType)}`;
    parts.push(s);
  } else {
    let s = pestText ? `טיפול ב${pestText}` : 'טיפול הדברה';
    if (byKey.scope?.value === 'recurring_problem') s += ' (הבעיה חוזרת)';
    parts.push(s);
    if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  }
  return parts.join(', ');
}

// ── הנדימנים: flat, but jobType='multiple' + taskCount need special gluing so it doesn't
// read as two disconnected facts.
function buildHandymanNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.jobType) {
    let s = answerText(byKey.jobType);
    if (byKey.jobType.value === 'multiple' && byKey.taskCount && byKey.taskCount.value !== 'one') {
      s += ` (${answerText(byKey.taskCount)})`;
    }
    parts.push(s);
  } else if (byKey.taskCount) {
    parts.push(answerText(byKey.taskCount));
  }
  if (byKey.duration && byKey.duration.value !== 'unsure') parts.push(`בסביבות ${answerText(byKey.duration)}`);
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  if (byKey.materialsNeeded?.value === 'missing_some') parts.push('חסר קצת חומרים/אביזרים');
  else if (byKey.materialsNeeded?.value === 'need_all') parts.push('צריך שההנדימן יביא את כל החומרים');
  return parts.join(', ');
}

// ── מובילים: flat, glues elevator access / packing / storage into natural asides.
function buildMoversNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.moveType) parts.push(answerText(byKey.moveType));
  if (byKey.distance) parts.push(answerText(byKey.distance));
  const elevator = byKey.elevatorStatus?.value;
  if (elevator === 'none') parts.push('בלי מעלית בשתי הכתובות');
  else if (elevator === 'one') parts.push('עם מעלית רק בכתובת אחת');
  else if (elevator === 'both') parts.push('עם מעלית בשתי הכתובות');
  else if (elevator === 'ground') parts.push('קומת קרקע, אין צורך במעלית');
  else if (byKey.elevatorStatus) parts.push(answerText(byKey.elevatorStatus));
  const packing = byKey.packing?.value;
  if (packing === 'full') parts.push('כולל שירות אריזה מלאה');
  else if (packing === 'partial') parts.push('כולל אריזת פריטים עדינים');
  else if (packing && packing !== 'none') parts.push(answerText(byKey.packing!));
  if (byKey.storage?.value === 'yes') parts.push('עם צורך באחסון זמני');
  else if (byKey.storage && byKey.storage.value !== 'no') parts.push(answerText(byKey.storage));
  if (byKey.specialItems && byKey.specialItems.value !== 'none') parts.push(`כולל ${answerText(byKey.specialItems)}`);
  return parts.join(', ');
}

// ── קבלני-אלומיניום: requestType branches into new (productType+quantity) or repair
// (repairType).
function buildAluminumNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const isNew = byKey.requestType?.value === 'new';
  if (isNew) {
    let s = byKey.productType ? answerText(byKey.productType) : 'התקנה חדשה';
    if (byKey.quantity && byKey.quantity.value !== 'not_sure') s += ` (${answerText(byKey.quantity)})`;
    parts.push(s);
  } else if (byKey.repairType) {
    parts.push(`תיקון: ${answerText(byKey.repairType)}`);
  } else if (byKey.requestType) {
    parts.push(answerText(byKey.requestType));
  }
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  return parts.join(', ');
}

// ── מדביקי-טפטים: scope branches into wall/room/multi, each glued into its own clause.
function buildWallpaperNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const scope = byKey.scope?.value;
  if (scope === 'wall') {
    parts.push('הדבקת טפט לקיר אחד/קיר דגש' + (byKey.roomType ? ` ב${answerText(byKey.roomType)}` : ''));
  } else if (scope === 'room') {
    parts.push('הדבקת טפט מלאה' + (byKey.roomType ? ` ל${answerText(byKey.roomType)}` : ''));
  } else if (scope === 'multi') {
    let s = 'הדבקת טפט למספר חדרים/לכל הדירה';
    if (byKey.size) s += ` בגודל ${answerText(byKey.size)}`;
    parts.push(s);
  } else if (byKey.scope) {
    parts.push(answerText(byKey.scope));
  }
  if (byKey.wallpaperSource?.value === 'need_choose') parts.push('צריך גם עזרה בבחירת הטפט');
  else if (byKey.wallpaperSource?.value === 'browsing') parts.push('עדיין רוצה לראות דוגמאות טפט');
  if (byKey.wallCondition?.value === 'old_wallpaper') parts.push('כולל הסרת טפט ישן');
  else if (byKey.wallCondition?.value === 'needs_repair') parts.push('הקיר טעון תיקון לפני ההדבקה');
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  return parts.join(', ');
}

// ── קבלני-גבס: flat, workType+room glued as one clause, context/size/propertyType as asides.
function buildGypsumNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  let primary = byKey.workType ? answerText(byKey.workType) : '';
  if (byKey.room) primary = primary ? `${primary} ב${answerText(byKey.room)}` : `עבודה ב${answerText(byKey.room)}`;
  if (primary) parts.push(primary);
  if (byKey.context?.value === 'renovation_part') parts.push('כחלק משיפוץ כללי');
  else if (byKey.context && byKey.context.value !== 'standalone') parts.push(answerText(byKey.context));
  let sizeProp = '';
  if (byKey.size && byKey.size.value !== 'unsure') {
    let t = answerText(byKey.size);
    if (!/מ[״"׳]?ר|מטר/.test(t)) t += ' מ״ר';
    sizeProp = `בהיקף ${t}`;
  }
  if (byKey.propertyType) sizeProp = sizeProp ? `${sizeProp} ב${answerText(byKey.propertyType)}` : `ב${answerText(byKey.propertyType)}`;
  if (sizeProp) parts.push(sizeProp);
  return parts.join(', ');
}

// ── פיזיותרפיסטים: flat, mirrors buildDietitianNeed's shape (goal/who/format/engagement).
function buildPhysiotherapistNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.reason) parts.push(answerText(byKey.reason));
  if (byKey.age_group && byKey.age_group.value !== 'self') parts.push(answerText(byKey.age_group));
  if (byKey.format && byKey.format.value !== 'flexible') {
    if (byKey.format.value === 'home') parts.push('בביקור בית');
    else if (byKey.format.value === 'clinic') parts.push('במרפאה');
    else parts.push(answerText(byKey.format));
  }
  if (byKey.process && byKey.process.value !== 'unsure') {
    if (byKey.process.value === 'assessment') parts.push('לצורך הערכה ראשונית');
    else if (byKey.process.value === 'ongoing') parts.push('במסגרת טיפול מתמשך');
    else parts.push(answerText(byKey.process));
  }
  return parts.join(', ');
}

// ── זגגים: requestType branches into repair (brokenItem) or new_install (newItem).
function buildGlazierNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const isRepair = byKey.requestType?.value === 'repair';
  const item = isRepair ? byKey.brokenItem : byKey.newItem;
  if (item) parts.push(isRepair ? `תיקון/החלפת ${answerText(item)}` : `התקנת ${answerText(item)}`);
  else if (byKey.requestType) parts.push(answerText(byKey.requestType));
  if (byKey.glassType && byKey.glassType.value !== 'not_sure') parts.push(`מסוג ${answerText(byKey.glassType)}`);
  if (byKey.size && byKey.size.value !== 'not_sure') {
    let t = answerText(byKey.size);
    if (!/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
    parts.push(`בגודל ${t}`);
  }
  return parts.join(', ');
}

// ── גגנים: requestType branches into an active leak (leakIssue) or a planned project
// (projectType) — mirrors קבלני-איטום's pattern closely.
function buildRoofingNeed(byKey: Record<string, Answer>): string {
  const v3 = buildRoofV3Need(byKey, 'גג'); if (v3) return v3;
  const parts: string[] = [];
  const isLeak = byKey.requestType?.value === 'active_leak';
  if (isLeak) {
    const issue = byKey.leakIssue ? answerText(byKey.leakIssue) : '';
    parts.push(issue ? `נזק/נזילה בגג – ${issue}` : 'נזק או נזילה פעילה בגג');
  } else if (byKey.projectType) {
    parts.push(answerText(byKey.projectType));
  } else if (byKey.requestType) {
    parts.push(answerText(byKey.requestType));
  }
  if (byKey.roofType && byKey.roofType.value !== 'not_sure') parts.push(`גג ${answerText(byKey.roofType)}`);
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  if (byKey.size && byKey.size.value !== 'not_sure') {
    let t = answerText(byKey.size);
    if (!/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
    parts.push(t);
  }
  if (byKey.urgency?.value === 'now' && !isLeak) parts.push('באופן דחוף');
  else if (byKey.urgency?.value === 'season') parts.push('לפני עונת הגשמים');
  return parts.join(', ');
}

// ── נוטריונים: flat (service/language/abroad/quantity/urgency).
function buildNotaryNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.service) parts.push(answerText(byKey.service));
  if (byKey.language && byKey.service?.value === 'translation') parts.push(`לשפה ${answerText(byKey.language)}`);
  if (byKey.abroad?.value === 'abroad') parts.push('לשימוש בחו"ל');
  if (byKey.quantity && !['one', 'notsure'].includes(byKey.quantity.value || '')) parts.push(answerText(byKey.quantity));
  return parts.join(', ');
}

// ── פחחי-רכב: damageType is the core clause; carPart/vehicleCondition/insurance appended
// as natural asides (mirrors buildGarageNeed).
function buildAutoBodyNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.damageType) parts.push(answerText(byKey.damageType));
  if (byKey.carPart && byKey.carPart.value !== 'not_sure') parts.push(`ב${answerText(byKey.carPart)}`);
  if (byKey.vehicleCondition?.value === 'needs_tow') parts.push('הרכב לא נוסע ומצריך גרר');
  else if (byKey.vehicleCondition?.value === 'second_opinion') parts.push('רוצה חוות דעת נוספת');
  else if (byKey.vehicleCondition && byKey.vehicleCondition.value !== 'drivable') parts.push(answerText(byKey.vehicleCondition));
  if (byKey.insurance?.value === 'yes') parts.push('דרך ביטוח');
  else if (byKey.insurance?.value === 'no') parts.push('בתשלום פרטי');
  else if (byKey.insurance && byKey.insurance.value !== 'not_sure') parts.push(answerText(byKey.insurance));
  if (byKey.vehicleType) parts.push(`ב${answerText(byKey.vehicleType)}`);
  return parts.join(', ');
}

// ── מנעולני-רכב: situation is a 6-way fork, each needing its own phrase; safety/spare_key/
// key_programming_context add urgency/context signal where relevant.
function buildCarLocksmithNeed(byKey: Record<string, Answer>): string {
  const situation = byKey.situation?.value;
  const parts: string[] = [];
  if (situation === 'locked_out_now') {
    let s = 'ננעלתי מחוץ לרכב עכשיו';
    if (byKey.safety?.value === 'child') s += ', יש ילד/תינוק בפנים - דחוף מאוד';
    else if (byKey.safety?.value === 'pet') s += ', יש חיית מחמד בפנים';
    parts.push(s);
  } else if (situation === 'lost_all_keys') parts.push('איבדתי את כל המפתחות לרכב');
  else if (situation === 'key_broken') parts.push('מפתח שבור/לא עובד');
  else if (situation === 'remote_not_working') parts.push('השלט/פיקוד מרחוק לא עובד');
  else if (situation === 'ignition_steering') parts.push('בעיה בלוק הגה/מתנע');
  else if (situation === 'key_programming_used_car') parts.push('קידוד מפתח לרכב יד שנייה שקניתי');
  else if (byKey.situation) parts.push(answerText(byKey.situation));

  if (situation !== 'locked_out_now') {
    if (byKey.spare_key?.value === 'no') parts.push('אין אף מפתח רזרבי');
    else if (byKey.spare_key?.value === 'yes') parts.push('יש מפתח רזרבי');
  }
  if (situation === 'key_programming_used_car' && byKey.key_programming_context) {
    const v = byKey.key_programming_context.value;
    if (v === 'none') parts.push('לא קיבלתי אף מפתח מהמוכר');
    else if (v === 'one_working') parts.push('יש מפתח אחד עובד');
    else if (v === 'old_not_working') parts.push('יש מפתח ישן שלא עובד');
    else if (v !== 'unsure') parts.push(answerText(byKey.key_programming_context));
  }
  if (byKey.key_type && byKey.key_type.value !== 'unsure') parts.push(answerText(byKey.key_type));
  if (byKey.car_make && byKey.car_make.value !== 'unsure') parts.push(answerText(byKey.car_make));
  return parts.join(', ');
}

// ── שמאי-רכב: flat (purpose/vehicleType/damageSeverity/reportType).
function buildCarAppraiserNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  let primary = byKey.purpose ? answerText(byKey.purpose) : '';
  if (byKey.vehicleType) {
    const t = answerText(byKey.vehicleType);
    primary = primary ? `${primary}, ב${t}` : `ב${t}`;
  }
  if (primary) parts.push(primary);
  if (byKey.damageSeverity && byKey.damageSeverity.value !== 'no_damage') parts.push(answerText(byKey.damageSeverity));
  if (byKey.reportType) parts.push(`נדרש ${answerText(byKey.reportType)}`);
  return parts.join(', ');
}

// ── חשמלאי-רכב: requestType branches into batterySymptom/faultDetail/installType.
function buildAutoElectricianNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const detail = byKey.batterySymptom || byKey.faultDetail || byKey.installType;
  if (detail) parts.push(answerText(detail));
  else if (byKey.requestType) parts.push(answerText(byKey.requestType));
  if (byKey.vehicleType) parts.push(`ב${answerText(byKey.vehicleType)}`);
  return parts.join(', ');
}

// ── מעסים: flat (goal/format/client_type/duration).
function buildMassageNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.goal) parts.push(answerText(byKey.goal));
  if (byKey.format && byKey.format.value !== 'flexible') {
    if (byKey.format.value === 'home') parts.push('בביקור בית');
    else if (byKey.format.value === 'clinic') parts.push('בקליניקה או בספא');
    else parts.push(answerText(byKey.format));
  }
  if (byKey.duration && byKey.duration.value !== 'unsure') parts.push(`למשך ${answerText(byKey.duration)}`);
  if (byKey.client_type && byKey.client_type.value === 'first_time') parts.push('פעם ראשונה עבורי');
  else if (byKey.client_type && !['regular', 'occasional'].includes(byKey.client_type.value || '')) parts.push(answerText(byKey.client_type));
  return parts.join(', ');
}

// ── קוסמטיקאיות: flat (treatment/concern/client_type/visit_type).
function buildCosmeticianNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  if (byKey.treatment) parts.push(`בנושא ${answerText(byKey.treatment)}`);
  if (byKey.concern && byKey.concern.value !== 'not_sure') parts.push(answerText(byKey.concern));
  if (byKey.client_type && byKey.client_type.value !== 'returning') {
    if (byKey.client_type.value === 'first_time') parts.push('פעם ראשונה אצל קוסמטיקאית');
    else if (byKey.client_type.value === 'switching') parts.push('רוצה להחליף קוסמטיקאית נוכחית');
    else parts.push(answerText(byKey.client_type));
  }
  if (byKey.visit_type && byKey.visit_type.value !== 'either') {
    if (byKey.visit_type.value === 'home_visit') parts.push('בביקור בית');
    else if (byKey.visit_type.value === 'at_salon') parts.push('במכון/קליניקה');
    else parts.push(answerText(byKey.visit_type));
  }
  return parts.join(', ');
}

// ── שמאי-רכוש: flat, purpose='insurance_claim' gets a richer clause with damageCause.
function buildAssetAppraiserNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const purposeVal = byKey.purpose?.value;
  const assetText = byKey.assetType ? answerText(byKey.assetType) : '';
  if (purposeVal === 'insurance_claim') {
    let clause = 'שומת נזק לתביעת ביטוח';
    if (byKey.damageCause) clause += ` בעקבות ${answerText(byKey.damageCause)}`;
    if (assetText) clause += ` (${assetText})`;
    parts.push(clause);
  } else if (byKey.purpose) {
    let clause = answerText(byKey.purpose);
    if (assetText) clause += ` (${assetText})`;
    parts.push(clause);
  } else if (assetText) parts.push(assetText);
  if (byKey.contextType?.value === 'business') parts.push('עבור עסק');
  else if (byKey.contextType?.value === 'institution') parts.push('עבור מוסד');
  else if (byKey.contextType && byKey.contextType.value !== 'private') parts.push(answerText(byKey.contextType));
  return parts.join(', ');
}

// ── קבלני-שיש: requestType branches into new/replace (productType+material) or repair.
function buildMarbleNeed(byKey: Record<string, Answer>): string {
  const parts: string[] = [];
  const req = byKey.requestType?.value;
  if (req === 'repair') {
    const repairText = byKey.repairType ? answerText(byKey.repairType) : 'תיקון שיש';
    parts.push(`תיקון: ${repairText}`);
  } else if (req === 'unsure' || (!req && byKey.requestType)) {
    // "unsure" or a typed ("אחר") request type — no productType exists for either
    // branch (showIf only reveals it for new/replace), so fall back to the raw text
    // instead of silently defaulting to "installation".
    parts.push(answerText(byKey.requestType));
  } else {
    let s = byKey.productType ? answerText(byKey.productType) : (req === 'replace' ? 'החלפת שיש' : 'התקנת שיש חדש');
    if (req === 'replace' && byKey.productType) s = `החלפת ${s}`;
    if (byKey.material && byKey.material.value !== 'no_preference') s += `, מ${answerText(byKey.material)}`;
    parts.push(s);
  }
  if (byKey.size && byKey.size.value !== 'not_sure') {
    let t = answerText(byKey.size);
    if (!/מ[״"׳]?ר/.test(t)) t += ' מ״ר';
    parts.push(`בגודל ${t}`);
  }
  if (byKey.propertyType) parts.push(`ב${answerText(byKey.propertyType)}`);
  return parts.join(', ');
}

// ── Dispatch by categorySlug rather than by sniffing outputKeys: with 20+ independently
// designed questionnaires now live, generic field names (scope/urgency/property_type/size/
// stage) are reused across unrelated categories with different meanings — a Set-based
// "does this answer set contain key X" check would misfire across categories. Keying by
// the category's own slug (already known at both call sites) sidesteps that entirely.
// current slug → legacy (pre-2026-07-22) slug, for CATEGORY_BUILDERS lookups below.
const LEGACY_SLUG_OF: Record<string, string> = Object.fromEntries(Object.entries(CATEGORY_SLUG_REDIRECTS).map(([legacy, current]) => [current, legacy]));

const CATEGORY_BUILDERS: Record<string, (byKey: Record<string, Answer>) => string> = {
  'שמאי-מקרקעין': buildRealEstateNeed,
  'אינסטלטורים': buildPlumbingNeed,
  'טכנאי-מזגנים': buildPlumbingNeed, // deliberately reuses plumbing's exact outputKeys
  'שרברבים': buildPlumbingNeed, // synonym for אינסטלטורים, identical questionnaire+outputKeys
  'עורכי-דין': buildLawyerNeed,
  'עורכי-דין-מקרקעין': buildLawyerNeed,
  'עורכי-דין-משפחה': buildLawyerNeed,
  'עורכי-דין-מסחרי': buildLawyerNeed,
  'עורכי-דין-נזיקין': buildLawyerNeed,
  'עורכי-דין-עבודה': buildLawyerNeed,
  'עורכי-דין-חובות': buildLawyerNeed,
  'עורכי-דין-תעבורה': buildLawyerNeed,
  'חשמלאים': buildElectricianNeed,
  'מוסכים': buildGarageNeed,
  'קבלני-שיפוצים': buildRenovationNeed,
  'רואי-חשבון': buildAccountantNeed,
  'יועצי-מס': buildTaxAdvisorNeed,
  'נגרים': buildCarpenterNeed,
  'מנעולנים': buildLocksmithNeed,
  'דיאטניות': buildDietitianNeed,
  'מעצבי-פנים': buildInteriorDesignNeed,
  'גננים': buildGardenerNeed,
  'וטרינרים': buildVetNeed,
  'טכנאי-מחשבים': buildComputerTechNeed,
  'יועצי-משכנתאות': buildMortgageAdvisorNeed,
  'רופאי-שיניים': buildDentistNeed,
  'מאמני-כושר': buildFitnessTrainerNeed,
  'צלמים': buildPhotographerNeed,
  'קבלני-איטום': buildWaterproofingNeed,
  'מדבירים': buildPestControlNeed,
  'הנדימנים': buildHandymanNeed,
  'מובילים': buildMoversNeed,
  'קבלני-אלומיניום': buildAluminumNeed,
  'מדביקי-טפטים': buildWallpaperNeed,
  'קבלני-גבס': buildGypsumNeed,
  'פיזיותרפיסטים': buildPhysiotherapistNeed,
  'זגגים': buildGlazierNeed,
  'גגנים': buildRoofingNeed,
  'נוטריונים': buildNotaryNeed,
  'פחחי-רכב': buildAutoBodyNeed,
  'מנעולני-רכב': buildCarLocksmithNeed,
  'שמאי-רכב': buildCarAppraiserNeed,
  'חשמלאי-רכב': buildAutoElectricianNeed,
  'מעסים': buildMassageNeed,
  'קוסמטיקאיות': buildCosmeticianNeed,
  'שמאי-רכוש': buildAssetAppraiserNeed,
  'קבלני-שיש': buildMarbleNeed,
  // 'מתווכים', 'סוכני-ביטוח', 'טכנאי-מוצרי-חשמל' and 'קבלני-שלד' deliberately omitted —
  // their option labels are self-contained noun-phrase clauses so the generic fallback
  // already reads naturally; no entry here means they fall through to it automatically.
};

function buildNeedFromAnswers(answers: Answer[], categorySlug?: string, servicePrefix?: string | null, yesNoIds?: ReadonlySet<string>): string | null {
  // 2026-09-23: category slugs went singular on 2026-07-22 (lib/categorySlugRedirects.ts)
  // but this table is still keyed by the old plural slugs — so every renamed category
  // silently fell through to the generic joiner. Resolve the current slug back to the
  // legacy key it was written under (one reverse map, built once).
  const builder = categorySlug ? (CATEGORY_BUILDERS[categorySlug] ?? CATEGORY_BUILDERS[LEGACY_SLUG_OF[categorySlug] ?? '']) : undefined;
  if (builder) {
    const byKey: Record<string, Answer> = {};
    for (const a of answers) if (!byKey[a.outputKey]) byKey[a.outputKey] = a;
    const text = builder(byKey);
    return text || null;
  }

  // Generic fallback for every other category's questionnaire (issue+urgency, etc).
  const parts = answers
    .map(a => {
      // City is shown separately (cityPart). Urgency chips become timingHint — BUT a custom
      // typed urgency (freeText, e.g. "תוך שבועיים") must still appear in the message body.
      if (a.outputKey === 'city') return null;
      if (a.outputKey === 'urgency' && !a.freeText) return null;
      const text = a.freeText || a.label;
      if (!text) return null;
      // Keep units clear in the message (e.g. "80–120 מ״ר")
      if (a.outputKey === 'size') return /מ["״׳']?ר/.test(text) ? text : `${text} מ״ר`;
      return text;
    })
    .filter(Boolean) as string[];
  // Service-cluster questionnaires: lead with the service's name unless an answer
  // already names it (avoids "עינית דיגיטלית, התקנת עינית דיגיטלית").
  // Keyword-lab topic quizzes (2026-09-30) open with a yes/no that restates the search ("האם הצורך הוא…?"): a bare
  // "כן" carries nothing the service name doesn't already say → dropped; a bare "לא" means the topic is NOT the need →
  // dropped together with the service prefix, so the message never claims the wrong subject.
  if (servicePrefix) {
    const bare = /^\s*(?:כן|לא)\s*$/;
    // bughunter 2026-09-30: a free-text "✏️ אחר" on the topic's yes/no question is ALSO a "not exactly this" — the visitor
    // named their own need, so the topic prefix must not be claimed next to it ("שומה למשכנתא, שומה לדירה בירושה").
    const saidNo = answers.some(a => a.outputKey !== 'city' && (a.freeText ? !!yesNoIds?.has(a.questionId) : a.label != null && /^\s*לא\s*$/.test(a.label)));
    for (let i = parts.length - 1; i >= 0; i--) if (bare.test(parts[i])) parts.splice(i, 1);
    if (!saidNo && !parts.some(p => p.includes(servicePrefix))) parts.unshift(servicePrefix);
    return parts.length ? parts.slice(0, 6).join(', ') : null;
  }
  if (servicePrefix && !parts.some(p => p.includes(servicePrefix))) parts.unshift(servicePrefix);
  return parts.length ? parts.slice(0, 6).join(', ') : null;
}

// builderSlug: slug to select a CATEGORY_BUILDERS clause-builder — undefined for
// service-cluster questionnaires (their outputKeys don't match the category builder,
// so they use the generic comma-join fallback). localStorage keeps the REAL slug.
let _cityChosenNow = false; // set by an explicit city pick; consumed by the next savePartialCtx
function savePartialCtx(answers: Answer[], categorySlug: string, categoryName: string | null, sessionId: string, builderSlug?: string, servicePrefix?: string | null, presetCity?: { name: string; lat: number; lng: number } | null, yesNoIds?: ReadonlySet<string>) {
  try {
    const cityAns = answers.find(a => a.outputKey === 'city');
    const urgencyAns = answers.find(a => a.outputKey === 'urgency');
    const cleanNeed = buildNeedFromAnswers(answers, builderSlug, servicePrefix, yesNoIds);
    const existing = localStorage.getItem(SMART_INTAKE_KEY);
    const prev = existing ? JSON.parse(existing) : {};
    // 2026-08-19 bug fix (user report, live on /אינסטלטור/נתניה then /אינסטלטור/פתח-תקווה
    // showing each other's city): localStorage[SMART_INTAKE_KEY] is ONE shared key across
    // the whole site, not scoped per page. The old `prev.categorySlug !== categorySlug`
    // guard only blocks a DIFFERENT category's leftovers — it does nothing for two
    // category×city pages of the SAME category (e.g. אינסטלטור/רמת-גן visited earlier,
    // then אינסטלטור/נתניה). When this page's questionnaire has NO fresh city answer
    // (the normal case on a /{category}/{city} page — the city is already known from the
    // URL, so that question is skipped/preset, not literally answered), `cityName` used
    // to fall back to `prev.city` — the PREVIOUS page's city — and silently overwrite the
    // shared store with it, which CategoryExpertsList then trusted over its own correct
    // presetCity. Fixed: this page's OWN presetCity (if any) is now the fallback instead
    // of blindly trusting whatever the store already had.
    const cityName = cityAns?.freeText || cityAns?.label || presetCity?.name || (presetCity ? null : prev.city) || null;
    // Resolve coordinates from the local dataset (custom cities get geocoded later, client-side)
    const geo = cityName ? findCity(cityName) : (presetCity && presetCity.name === cityName ? presetCity : null);
    // 2026-08-19 follow-up fix (user report: /אינסטלטור/נתניה visibly shows נתניה for a
    // moment, then flips to פתח תקווה — a genuine cross-page race, not just a leftover
    // write. useSmartIntakeCtx hydrates asynchronously: the FIRST client read of
    // localStorage can still be whatever a DIFFERENT city page wrote seconds/minutes
    // earlier, arriving in CategoryExpertsList a beat after its first paint (which
    // correctly used presetCity). Comparing raw values can't tell "genuinely fresh" apart
    // from "old stale value that happens to differ from presetCity" — so a real
    // timestamp is stamped on every write that resolves to a definitive city (either a
    // live answer or this page's own known presetCity). CategoryExpertsList then only
    // trusts a store value stamped AFTER its own mount, which an old cross-page write can
    // never satisfy.
    const isDefinitiveCity = !!cityAns || !!presetCity;
    // 2026-08-23 (user report): a DIFFERENT category's leftover need/free-text must never
    // survive under this category's own slug — e.g. answering a plumbing questionnaire
    // fully, then only reaching the city question on an electrician page (no fresh
    // cleanNeed produced yet) used to fall back to `prev.cleanNeed`/`prev.customNeed`
    // regardless of which category they came from, silently relabeling the plumbing need
    // as the electrician's own. City/timing/price are legitimately reusable across
    // categories (same visitor, same location/urgency) — only category-SPECIFIC content
    // (cleanNeed, customNeed) is category-scoped and must be dropped on a category switch.
    const sameCategoryAsPrev = !prev.categorySlug || prev.categorySlug === categorySlug;
    localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({
      ...prev,
      categorySlug,
      categoryName: categoryName || categorySlug,
      cleanNeed: cleanNeed ?? (sameCategoryAsPrev ? prev.cleanNeed : null) ?? null,
      customNeed: sameCategoryAsPrev ? (prev.customNeed ?? null) : null,
      city: cityName,
      cityLat: geo ? geo.lat : (cityName === prev.city ? prev.cityLat ?? null : null),
      cityLng: geo ? geo.lng : (cityName === prev.city ? prev.cityLng ?? null : null),
      cityAnsweredAt: isDefinitiveCity ? Date.now() : (prev.cityAnsweredAt ?? null),
      // a non-choice write that CHANGES the city must drop the old choice stamp, or other tabs read it as a fresh pick (re-verify #6)
      cityChosenAt: _cityChosenNow && cityAns ? Date.now() : (cityName === prev.city ? prev.cityChosenAt ?? null : null),
      timingHint: urgencyAns?.label || prev.timingHint || null,
      priceHint: prev.priceHint || null,
      sessionId,
    }));
    _cityChosenNow = false;
    notifySmartIntake(); // every listener sees it on this tick (race fix 2026-09-28)
  } catch {}
}

// Per-category example for the "write it yourself" free-text mode.
const REQUEST_EXAMPLES: Record<string, string> = {
  'שמאי-מקרקעין': 'אשמח להצעת מחיר לשמאות לוילה בהרצליה בגודל של 230 מ״ר. אני צריך את זה בשבוע הקרוב, השומה צריכה להיות לבנק לאומי ולהיכלל ברשימות של הבנק.',
  'חשמלאים': 'אשמח להצעת מחיר להתקנת לוח חשמל חדש בדירת 4 חדרים בתל אביב. יש גם כמה נקודות חשמל להוסיף, ואני צריך את זה בשבוע הקרוב.',
  'אינסטלטורים': 'אשמח להצעת מחיר לטיפול בנזילה במטבח ובהחלפת ברז באמבטיה בדירה ברמת גן. רצוי בימים הקרובים.',
};
function requestExample(categorySlug: string, service: string): string {
  return REQUEST_EXAMPLES[categorySlug]
    || `אשמח להצעת מחיר ל${service}. פרטו כאן בדיוק מה נדרש, היכן, באיזה היקף ומתי תרצו את השירות.`;
}

function saveCustomCtx(text: string, categorySlug: string, categoryName: string | null, sessionId: string) {
  try {
    const existing = localStorage.getItem(SMART_INTAKE_KEY);
    const prev = existing ? JSON.parse(existing) : {};
    // Same category-scoping fix as savePartialCtx above — a category switch drops the
    // PREVIOUS category's leftover cleanNeed too, not just customNeed (this function only
    // ever writes customNeed itself; cleanNeed here is purely inherited from `prev`).
    const sameCategoryAsPrev = !prev.categorySlug || prev.categorySlug === categorySlug;
    localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify({
      ...prev, categorySlug, categoryName: categoryName || categorySlug,
      cleanNeed: sameCategoryAsPrev ? (prev.cleanNeed ?? null) : null,
      customNeed: text.trim() || null, sessionId,
    })); notifySmartIntake();
  } catch {}
}

interface Props {
  questionnaire: Questionnaire;
  categoryName: string | null;
  proCity?: string | null;
  // City×category landing pages pre-answer the city question via the URL — skip asking it.
  presetCity?: { name: string; lat: number; lng: number };
  // Location-independent category/article (e.g. remote mortgage advice) — drop the city
  // question entirely rather than pre-answering it. Unlike presetCity, no value is set.
  skipCityQuestion?: boolean;
  // Nationwide category (Founder 2026-09-22): the city is the LAST question, worded "באיזו עיר צריך את השירות?" (neutral, gender law) — it feeds the
  // message ("ואני מ<עיר>"), not the matching.
  cityLast?: boolean;
  multi?: boolean; // category page = messages for MANY experts (plural copy)
  onComplete?: (ctx: Partial<SmartIntakeContext>) => void;
  onStart?: () => void;
  // Compact-design (questionnaire_design='compact') condensed rendering: single-row
  // horizontally-scrollable chips + progress dots, used inside the pinned scroll card
  // (CompactIntake). Same state/handlers/message logic — presentation only. The classic
  // render path below is untouched when this is false/omitted.
  condensed?: boolean;
  // Condensed-only style overrides for the question+dots row (2026-07-23,
  // QuickConsultBar's "🎯 שאלות לייעוץ מדויק" mode — lets the question text visually take
  // over the same blue-gradient header band QuickConsultBar otherwise shows its own
  // heading in, so the bar's overall shape never changes between modes). Omitted
  // everywhere else — CompactIntake's usage is visually untouched.
  condensedHeaderBg?: string;
  condensedHeaderColor?: string;
  // condensedHeaderBg-only: called instead of nothing when "back" is pressed on the very
  // FIRST question (normally there's nowhere to go back to internally) — lets the caller
  // exit this questionnaire entirely back to whatever it showed before (QuickConsultBar's
  // "🎯 מדויק" toggle, 2026-07-23 user request: "leave a way back even on question 1").
  onExitAtStart?: () => void;
  // Fired the moment the city question is answered (before advancing to the next step,
  // if any) — lets the host show matching pros for that city inline, without waiting for
  // the whole questionnaire to finish (2026-07-30 user request: "the moment the user
  // picks a city, show the experts near them" — city isn't always the last question).
  onCityAnswered?: (name: string, coords?: { lat: number; lng: number }) => void;
  // Single-pro mode (2026-08-23 user request): this bar is scoped to ONE professional
  // (their own page), not a category-wide pool — the city question must validate the
  // chosen city against THIS pro's real service radius instead of accepting any city
  // in Israel. Omitted everywhere else (category pages, multi-pro pool) since there's
  // no single "this professional's radius" to check there.
  singleProName?: string;
  singleProCoords?: { lat: number; lng: number } | null;
  // 2026-09-22: the pro's own service-area override (service_cities / service_radius_km)
  // — beats DEFAULT_RADIUS_KM below when set (lib/proMatching.ts serviceAreaVerdict).
  singleProService?: { serviceCities: string | null; serviceRadiusKm: number | null } | null;
  // condensedHeaderBg-only PREVIEW layouts (Founder 01/10/26, ?layout= on kwlab topic landings, lib/topicLayout.ts): 'one' = question band +
  // ONE row of chips (~84px); 'row' = a single row (label + chips, ~58px). Omitted = the standard 2-row bar, untouched.
  barLayout?: 'one' | 'row' | 'seg' | 'list'; // 'seg' = 3.x segmented track (no emoji); 'list' = 3.x bottom-sheet option rows
  // 3.6 Mad-Libs (?layout=3.6): the host reads every question + its answer label, and can jump the wizard to any step (a blank tapped)
  onProgress?: (p: { step: number; items: { id: string; isCity: boolean; question: string; label: string | null }[] }) => void;
  jump?: { step: number; n: number } | null;
}

export default function QuestionnaireWizard({ questionnaire, categoryName, proCity, presetCity, skipCityQuestion, cityLast, multi, onComplete, onStart, condensed, condensedHeaderBg, condensedHeaderColor, onExitAtStart, onCityAnswered, singleProName, singleProCoords, singleProService, barLayout, onProgress, jump }: Props) {
  const { questions, version, categorySlug, articleId } = questionnaire;
  // Service-cluster questionnaires use the generic WA clause joiner (their outputKeys
  // are cluster-specific); category questionnaires keep their dedicated builder.
  const needSlug = questionnaire.serviceCluster ? undefined : categorySlug;
  const servicePrefix = questionnaire.serviceCluster ? (questionnaire.serviceName || null) : null;
  // questions whose only options are כן/לא (keyword-lab topic Q1) — a free-text answer there replaces the topic prefix
  const yesNoIds = useMemo(() => new Set(questions.filter(q => q.options?.length === 2 && q.options.every(o => /^\s*(?:כן|לא)\s*$/.test(o.label || ''))).map(q => q.id)), [questions]);
  const startedRef = useRef(false);
  const fireStart = () => { if (!startedRef.current) { startedRef.current = true; onStart?.(); } };
  const serviceWord = categoryService(categoryName);
  const [freeMode, setFreeMode] = useState(false);
  const [freeText, setFreeText] = useState('');

  const [step, setStep] = useState(0);
  const stepRef = useRef(0); stepRef.current = step;
  const doneRef = useRef(false);
  const answeredN = useRef(0);
  const [answerMap, setAnswerMap] = useState<Record<string, Answer>>({});

  // 2026-08-24 bug fix (user report: the FIRST question visibly flashes, then swaps to
  // a DIFFERENT question a moment later, with no user interaction — reported on
  // טכנאי-מזגנים but confirmed to be a generic engine bug affecting every questionnaire).
  // Root cause: `presetCity` on a category page with no city in the URL comes from
  // QuickConsultBar's `knownCity` — derived from the shared smartIntakeCtx CLIENT store
  // (useSyncExternalStore), which hydrates ASYNCHRONOUSLY (server snapshot is always
  // null). At the component's first paint, `presetCity` is often still null/undefined;
  // a poll tick (up to 500ms later) can then resolve it to a real city — WITHOUT this
  // component remounting (QuickConsultBar only remounts it via `key={cityResetNonce}`
  // on an explicit "other city" pick, not on this kind of async resolution). The
  // `allQuestions` useMemo below used to depend on the LIVE `presetCity` value, so that
  // resolution reactively inserted/removed the city question from the array WHILE THE
  // VISITOR WAS ALREADY LOOKING AT STEP 0 — the exact same numeric `step` index
  // suddenly pointed at a different question. Fix: whether to insert the city question
  // at all is now decided ONCE, from whatever `presetCity`/`skipCityQuestion` were at
  // MOUNT time, and never re-evaluated afterward for the life of this component
  // instance — only a genuine remount (new `key`, e.g. the explicit city-reset flow)
  // re-decides it, which is exactly when a fresh decision is actually correct. Answers
  // (`answerMap`) still reactively affect `showIf` filtering as before — that part was
  // never the bug, only the presence/position of the CITY question itself was.
  const shouldSkipCityRef = useRef<boolean | null>(null);
  if (shouldSkipCityRef.current === null) {
    // Founder LAW (2026-09-27, appraiser questionnaire skipped the city for a returning visitor): the city question is
    // MANDATORY in every questionnaire, every category, every page — a remembered / URL / nationwide city is only the
    // FIRST CHIP (one-tap confirm), never a reason to skip. `skipCityQuestion` and hook skip_city are ignored by design.
    shouldSkipCityRef.current = false; void presetCity; void skipCityQuestion;
  }
  // 2026-08-24 bug fix (user report: after answering the first question, the
  // questionnaire silently jumped straight to "ready to send" instead of the expected
  // showIf-gated follow-up question — reproduced exactly on טכנאי-מזגנים but confirmed
  // generic: ANY questionnaire with a showIf-gated follow-up hits this). Root cause:
  // `advance()`/`orderedAnswers()` compared `step + 1` against the MEMOIZED
  // `allQuestions.length` from the CURRENT render — computed from `answerMap` as it
  // stood BEFORE this answer was applied. `setAnswerMap(newMap)` only SCHEDULES a
  // re-render; the memo doesn't recompute until then, but `advance()` runs synchronously
  // in the same tick, using the STALE array — one that doesn't include the just-revealed
  // follow-up question yet (its showIf hadn't matched anything until this exact answer).
  // So a 2-question array with no matches yet + step already at index 1 made
  // `step+1 (2) >= allQuestions.length (2)` true — false-triggering `finalize()` a full
  // question early. Fix: extracted the array-building logic into `buildAllQuestions(map)`
  // so callers that just produced a NEW answer map (advance, orderedAnswers) can compute
  // the CURRENT, up-to-date question list directly from that map instead of reading the
  // one-render-behind memoized value.
  const buildAllQuestions = useCallback((map: Record<string, Answer>): Question[] => {
    const rest = questions.filter(q => q.outputKey !== 'city');
    let withCity = rest;
    if (!shouldSkipCityRef.current) {
      // 2026-07-29 (user request): when the questionnaire opens with the article's own
      // hook question, the city question comes SECOND (hook → city → detail questions) —
      // the visitor states WHAT they need before WHERE. Without a hook (plain category
      // questionnaire) city stays first, as before.
      // Keyword-lab topic quizzes (Founder 2026-09-30, [[experts-quiz-pull-psychology]]): the easy "כן" first, the topic
      // questions, THEN the city — every answer makes the next one cheaper.
      const cityAt = String(version || '').startsWith('kwlab-') ? rest.length : rest[0]?.id === 'hook' ? 1 : 0;
      // first chip (bughunter #7/#14 2026-09-28): the city already chosen in this questionnaire; on a pro's own page his city
      // (then the cities around him), never a remembered city he does not serve; else the page / remembered city
      const firstCity = map['q_city']?.label || (singleProCoords ? proCity : null) || presetCity?.name || proCity;
      withCity = cityLast
        ? [...rest, { ...buildCityQuestion(firstCity, categoryName || ''), question: 'באיזו עיר צריך את השירות?' }] // nationwide (gender law 01/10: was the slash form, now neutral): city LAST, still asked
        : [...rest.slice(0, cityAt), buildCityQuestion(firstCity, categoryName || ''), ...rest.slice(cityAt)];
    }
    const shown = withCity.filter(q => !q.showIf || matchesShowIf(q.showIf, map));
    // Founder LAW (2026-09-23, re-broken via article hooks, caught 2026-09-27 on s_muj8r0jle7mwxrbe): never more than 2
    // questions besides the city — the article hook COUNTS as one. Keep order, drop everything past the 2nd non-city question.
    let n = 0; return shown.filter(q => { if (q.outputKey === 'city') return true; n++; return n <= 2; });
  }, [questions, version, proCity, presetCity?.name, categoryName, cityLast, singleProCoords]);
  const allQuestions = useMemo<Question[]>(() => buildAllQuestions(answerMap), [buildAllQuestions, answerMap]);
  // bughunter F2 (2026-09-27): M of "שאלה N מתוך M" = the longest path the visitor can still take (branches counted), so the
  // counter and dots never grow mid-flow. Answered branches are already in allQuestions; unanswered parents add their largest branch.
  const totalSteps = useMemo(() => {
    const shownIds = new Set(allQuestions.map(q => q.id));
    const answered = new Set(Object.keys(answerMap));
    const groups: Record<string, number> = {};
    for (const q of questions) { if (!q.showIf || shownIds.has(q.id) || q.outputKey === 'city') continue; const parent = (q.showIf as any).questionId ?? (q.showIf as any).key ?? ''; if (answered.has(parent)) continue; const key = `${parent}|${JSON.stringify((q.showIf as any).value ?? (q.showIf as any).values ?? '')}`; groups[key] = (groups[key] || 0) + 1; }
    const extra = Math.max(0, ...Object.values(groups));
    const nonCity = allQuestions.filter(q => q.outputKey !== 'city').length;
    return allQuestions.length + Math.min(extra, Math.max(0, 2 - nonCity)); // the ≤2 law caps the growth too
  }, [allQuestions, answerMap, questions]);

  const [inlineOther, setInlineOther] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [animIn, setAnimIn] = useState(true);
  const [animDir, setAnimDir] = useState<1 | -1>(1);
  const [done, setDone] = useState(false);
  doneRef.current = done;
  // 14/10/26 funnel: quiz_shown once per mount; quiz_abandoned = the visitor left (tab hidden / page left) mid-quiz, with the last step reached
  useEffect(() => {
    fx('quiz_shown', { cat: categorySlug, steps: questions.length });
    let sent = false;
    const ab = () => { if (sent || doneRef.current) return; sent = true; fx('quiz_abandoned', { cat: categorySlug, last_step: stepRef.current, answered: answeredN.current }); };
    const onVis = () => { if (document.visibilityState === 'hidden') ab(); else sent = false; };
    document.addEventListener('visibilitychange', onVis, { passive: true } as any); window.addEventListener('pagehide', ab, { passive: true } as any);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', ab); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cityOtherOpen, setCityOtherOpen] = useState(false);
  // Single-pro mode: set the instant a chosen city resolves outside this pro's real
  // service radius — replaces the normal question UI with a redirect notice instead of
  // silently continuing a questionnaire that can never lead anywhere for this pro.
  const [outOfAreaCity, setOutOfAreaCity] = useState<string | null>(null);
  const sessionId = useRef<string>(genSession());

  // 2026-08-19 (user report, /אינסטלטור/נתניה momentarily flashing then flipping to a
  // stale city from a previously-visited city page of the same category): a page with a
  // presetCity never called savePartialCtx at all until the visitor answered/skipped
  // something — so the shared store could sit on another page's stale city for the
  // visitor's ENTIRE time on this page if they never interacted. Proactively correct the
  // store to this page's own known city the moment it mounts, instead of waiting for an
  // interaction that may never come — paired with the cityAnsweredAt timestamp guard in
  // CategoryExpertsList, which needs this write to happen (and be freshly stamped) for
  // the flash to resolve to the RIGHT city instead of staying wrong indefinitely.
  useEffect(() => {
    // only when nothing newer was chosen — a remount after a pick must not write the page's URL city over the visitor's choice
    if (presetCity && (resolvePageCity() ?? presetCity.name) === presetCity.name) savePartialCtx([], categorySlug, categoryName, sessionId.current, needSlug, servicePrefix, presetCity, yesNoIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetCity?.name]);

  // Ordered answers, in question order (for message building). Uses buildAllQuestions(map)
  // — NOT the memoized `allQuestions` — so a caller passing a just-updated map (advance(),
  // finalize()) gets the CURRENT question list for that map, not last render's stale one
  // (see buildAllQuestions's comment for the full bug this fixes).
  const orderedAnswers = useCallback(
    (map: Record<string, Answer>) => buildAllQuestions(map).map(q => map[q.id]).filter(Boolean) as Answer[],
    [buildAllQuestions]
  );

  const log = useCallback(async (
    q: Question, value: string | null, label: string | null,
    freeText: string | null, isDropoff = false,
    eventType: 'view' | 'answer' | 'dropoff' = isDropoff ? 'dropoff' : 'answer'
  ) => {
    if (eventType === 'answer') { answeredN.current += 1; fx('quiz_step_answered', { step: stepRef.current, qid: q.id, cat: categorySlug }); } // 14/10/26 he_user_events funnel
    // the ONE questionnaire door (lib/trackClient.mjs — the 3D journey logs through the same function, 04/10/26); it attaches the
    // tracker session id itself (root fix 2026-09-27: orphan answer logs) and retries once on 5xx
    await questionnaireLog({ wizardId: sessionId.current, categorySlug, articleId, version, questionId: q.id, questionText: q.question,
      answerValue: value, answerLabel: label, freeText, isDropoff, eventType });
  }, [categorySlug, articleId, version]);

  // 2026-07-29 (admin questionnaire-stats): log a 'view' the first time each question
  // is actually shown this session — the pre-existing logging only ever recorded
  // answers + beforeunload dropoffs, so there was no denominator to compute a
  // per-question drop-off rate against. Guarded by a ref (not state) so it fires once
  // per question per mount, never re-fires on back/forward re-renders of the same step.
  const viewedSteps = useRef<Set<string>>(new Set());
  // 2026-07-30 (user-caught bug, screenshot): the condensedHeaderBg options-scroller div
  // is the SAME persistent DOM node across every question (React reuses it — it's not
  // conditionally keyed per question), so its scrollLeft was carrying over from
  // whatever question rendered before it. Combined with justify-content:'safe center'
  // (the 2026-07-29 city-chip-visibility fix), a leftover scroll offset from a wide
  // question (e.g. the 116-city list) "leaked" into the next, narrower question as
  // dead space before the first chip — exactly the reported symptom. Fix: deterministic
  // reset to scrollLeft=0 on every step change, independent of whatever CSS alignment
  // is in play — this can never carry stale scroll state forward, on any question, in
  // any browser, regardless of centering behavior.
  const optionsScrollerRef = useRef<HTMLDivElement>(null);
  // useLayoutEffect (not useEffect) — runs synchronously before the browser paints, so
  // the reset happens before the stale-scroll frame would ever be visible, not after.
  useLayoutEffect(() => {
    if (optionsScrollerRef.current) optionsScrollerRef.current.scrollLeft = 0;
  }, [step]);
  useEffect(() => {
    if (done) return;
    const q = allQuestions[step];
    if (!q || viewedSteps.current.has(q.id)) return;
    viewedSteps.current.add(q.id);
    log(q, null, null, null, false, 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, done, allQuestions]);

  useEffect(() => {
    if (done || step === 0) return;
    const q = allQuestions[step];
    const handler = () => { if (q) log(q, null, null, null, true); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step, done, allQuestions, log]);

  // ── CITY LISTENER (Founder 2026-09-28: "קוד מאזין בתוך השאלון, שיוכל להתמלא מכמה מקומות… בזמן אמת") ──
  // One source of truth for the visitor's city: the URL city of this page, else the shared store (written the moment ANY
  // surface learns it — the home smart search, the menu search, another questionnaire, a /<category>/<city> page). A known
  // city is FILLED IN as the city answer (logged, part of the message — the CITY LAW's "we must know the city" holds) and the
  // wizard moves to the next question; the city stays visible ("📍 אשקלון") and one tap / ‹ reopens the city question.
  // Two-way: a city picked here is written to the store by savePartialCtx, so every other surface updates live.
  const storeCtx = useSmartIntakeCtx();
  // ONE rule for "which city" on this page — lib/smartIntakeStore resolvePageCity (the badge, the expert list and the WhatsApp
  // button all read it; bughunter round-3 blocker: three readers with three rules showed three cities on one screen)
  const knownCityName = resolvePageCity(storeCtx) || presetCity?.name || null; // subscription = the trigger
  const [cityEditing, setCityEditing] = useState(false);
  const [autoCity, setAutoCity] = useState<string | null>(null);
  const cityIdx = allQuestions.findIndex(q => q.outputKey === 'city');
  const skippedIds = useRef<Set<unknown>>(new Set()); // questions the visitor skipped (דלג) — never "open" again for the auto-advance below
  useEffect(() => {
    const nowName = resolvePageCity(readSmartIntakeNow()) || presetCity?.name || null; // never act on a stale subscribed copy
    if (done || cityEditing || !nowName || cityIdx < 0) return;
    const c = findCity(nowName); if (!c) return; // a name we can't resolve is asked, not guessed
    if (singleProCoords) { // a pro's own page: only a city inside this pro's real service area may be pre-filled
      const km = proDistanceKm({ lat: singleProCoords.lat, lng: singleProCoords.lng, city: proCity ?? null }, c.lat, c.lng); // THE city-anchored km
      if (!proServesCity({ lat: singleProCoords.lat, lng: singleProCoords.lng, ...(singleProService || {}) } as any, km, c.name)) return;
    }
    const q = allQuestions[cityIdx];
    let map = answerMap;
    if (map[q.id]?.label !== c.name) {
      map = { ...answerMap, [q.id]: { questionId: q.id, outputKey: 'city', value: c.name, label: c.name, freeText: null } };
      setAnswerMap(map); setAutoCity(c.name);
      log(q, c.name, c.name, null);
      onCityAnswered?.(c.name, { lat: c.lat, lng: c.lng });
      savePartialCtx(orderedAnswers(map), categorySlug, categoryName, sessionId.current, needSlug, servicePrefix, presetCity, yesNoIds);
    }
    if (step === cityIdx) { // sitting on (or just arrived at) the city question with the city already known → first open question
      const qs = buildAllQuestions(map);
      // bughunter 02/10: a SKIPPED question is not "open" — counting it sent דלג → city → back to the same question (dead loop, 3.x)
      const nextOpen = qs.findIndex(x => x.outputKey !== 'city' && !map[x.id] && !skippedIds.current.has(x.id));
      if (nextOpen >= 0) setStep(nextOpen); else finalize(orderedAnswers(map));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownCityName, done, cityEditing, cityIdx, step]);
  const openCityChange = () => { setCityEditing(true); goToStep(cityIdx, -1); };
  // the chosen city stays visible (and one tap changes it) on every question after the city — auto-filled or picked here
  const cityBadge = !cityEditing && cityIdx >= 0 && step !== cityIdx ? (answerMap[allQuestions[cityIdx]?.id]?.label || null) : null; void autoCity;
  // condensed header fit (bughunter round 3 #2): reserve the badge's measured width, then shrink a long question (15→12px)
  // before it would ever be cut mid-word — the header height stays fixed
  const badgeRef = useRef<HTMLButtonElement>(null); const qTextRef = useRef<HTMLParagraphElement>(null);
  const [badgeW, setBadgeW] = useState(0); const [qFont, setQFont] = useState(15);
  const [badgeCompact, setBadgeCompact] = useState(false);
  useLayoutEffect(() => { setBadgeCompact(false); setQFont(15); }, [cityBadge, step]);
  useLayoutEffect(() => { setBadgeW(badgeRef.current ? Math.ceil(badgeRef.current.getBoundingClientRect().width) : 0); }, [cityBadge, step, badgeCompact]);
  // exact overflow (no +1 — a 1px overflow still draws the ellipsis); 15→12px, then the badge narrows to 92px with an ellipsis (name always visible; full name in its
  // label/title), then 11px — the fixed header height never changes (bughunter round 4 A: 4/67 categories clipped at 390)
  useLayoutEffect(() => {
    const el = qTextRef.current; if (!el || el.scrollWidth <= el.clientWidth) return;
    if (qFont > 12) setQFont(f => Math.max(12, f - 0.5));
    else if (cityBadge && !badgeCompact) setBadgeCompact(true);
    else if (qFont > 11) setQFont(11);
  }, [qFont, badgeW, step, cityBadge, badgeCompact]);

  // Defensive clamp: if a branch-changing answer shrinks allQuestions out from under the
  // current index (shouldn't happen in normal forward/back navigation — see plan notes —
  // but cheap insurance against an out-of-range read).
  const currentQ = allQuestions[Math.min(step, allQuestions.length - 1)];
  // ?layout=3.6 Mad-Libs hooks (no-ops unless the host passes onProgress / jump)
  const progressKey = onProgress ? JSON.stringify([step, done, allQuestions.map(q => q.id + '=' + (answerMap[q.id]?.label || ''))]) : '';
  useEffect(() => {
    if (!onProgress) return;
    onProgress({ step: done ? allQuestions.length : step, items: allQuestions.map(q => ({ id: q.id, isCity: q.outputKey === 'city', question: q.question, label: answerMap[q.id]?.label || null, options: visibleOptions(q.options).length })) });
  }, [progressKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!jump || jump.step < 0 || jump.step >= allQuestions.length) return;
    setDone(false);
    if (jump.step === cityIdx) setCityEditing(true);
    goToStep(jump.step, -1);
  }, [jump?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  function goToStep(nextStep: number, dir: 1 | -1) {
    setAnimDir(dir);
    setAnimIn(false);
    setTimeout(() => {
      setStep(nextStep);
      setInlineOther(false);
      setOtherText('');
      setCityOtherOpen(false);
      setAnimIn(true);
    }, 160);
  }

  function advance(newMap: Record<string, Answer>) {
    const ordered = orderedAnswers(newMap);
    // Save partial context → WA message updates in real time
    savePartialCtx(ordered, categorySlug, categoryName, sessionId.current, needSlug, servicePrefix, presetCity, yesNoIds);
    // buildCurrent(newMap), not the memoized `allQuestions` — see buildAllQuestions's
    // comment: this answer may have just revealed a showIf-gated follow-up question
    // that the STALE memo (computed from the map before this answer) doesn't know about
    // yet, which used to false-trigger finalize() one question too early.
    if (step + 1 >= buildAllQuestions(newMap).length) {
      finalize(ordered);
    } else {
      goToStep(step + 1, 1);
    }
  }

  async function handleCityAnswer(name: string, coords?: { lat: number; lng: number }) {
    // 2026-08-23 (user request): single-pro mode must validate against this pro's real
    // service radius — continuing the questionnaire for a city outside it produces a
    // lead this pro can never actually serve. Resolve coords (Google-autocomplete coords
    // if given, else the local dataset) and compare against DEFAULT_RADIUS_KM (the SAME
    // 25km radius the public listing/matching engine already uses, lib/israelCities.ts).
    // A city we can't resolve coords for at all (rare — a genuinely unlisted small town)
    // is let through rather than blocked, since we have no basis to reject it.
    if (singleProCoords) {
      const cityCoords = coords || (() => { const c = findCity(name); return c ? { lat: c.lat, lng: c.lng } : null; })();
      if (cityCoords) {
        const ca = cityAnchor(name, cityCoords.lat, cityCoords.lng);
        const distKm = proDistanceKm({ lat: singleProCoords.lat, lng: singleProCoords.lng, city: proCity ?? null }, ca.lat, ca.lng); // THE city-anchored km
        if (!proServesCity({ lat: singleProCoords.lat, lng: singleProCoords.lng, ...(singleProService || {}) } as any, distKm, name)) {
          setOutOfAreaCity(name);
          const redirectUrl = `/${encodeURIComponent(categorySlug)}/${citySlug(name)}`;
          setTimeout(() => { window.location.href = redirectUrl; }, 3000);
          return;
        }
      }
    }
    fireStart();
    const q = currentQ;
    await log(q, name, name, null);
    const newAnswer: Answer = { questionId: q.id, outputKey: 'city', value: name, label: name, freeText: null };
    setCityEditing(false); setAutoCity(null);
    _cityChosenNow = true; // the save below stamps cityChosenAt IN THE SAME WRITE — a later stamp lost the race to the listener
    notePagePick(name); // tab-local: this page's pick (resolvePageCity rule 2)
    const newMap = { ...answerMap, [q.id]: newAnswer };
    setAnswerMap(newMap);
    onCityAnswered?.(name, coords);
    // a city picked (first time OR via the 📍 badge from a later step) continues at the FIRST UNANSWERED question — never
    // "the step after the city", which on a city-LAST (nationwide) flow finished the questionnaire with nothing answered
    // (bughunter BLOCKER 2026-09-28, /יועץ-משכנתאות/ראשון-לציון → 📍 → חיפה → "מוכנה לשליחה")
    const qs = buildAllQuestions(newMap);
    const nextOpen = qs.findIndex(x => x.outputKey !== 'city' && !newMap[x.id]);
    if (nextOpen >= 0) {
      savePartialCtx(orderedAnswers(newMap), categorySlug, categoryName, sessionId.current, needSlug, servicePrefix, presetCity, yesNoIds);
      goToStep(nextOpen, nextOpen > step ? 1 : -1);
    } else advance(newMap); // everything else answered → finish (same as before)
    // Overwrite with exact coords from Google autocomplete (kept by later saves since city is unchanged)
    if (coords) {
      try {
        const s = localStorage.getItem(SMART_INTAKE_KEY);
        const c = s ? JSON.parse(s) : {};
        c.cityLat = coords.lat; c.cityLng = coords.lng;
        localStorage.setItem(SMART_INTAKE_KEY, JSON.stringify(c)); notifySmartIntake();
      } catch {}
    }
  }

  async function handleAnswer(opt: QOption | null, free?: string) {
    fireStart();
    const q = currentQ;
    await log(q, opt?.value ?? null, opt?.label ?? null, free || null);
    const newAnswer: Answer = { questionId: q.id, outputKey: q.outputKey, value: opt?.value ?? null, label: opt?.label ?? null, freeText: free || null };
    const newMap = { ...answerMap, [q.id]: newAnswer };
    setAnswerMap(newMap);
    advance(newMap);
  }

  async function handleSkip() {
    fireStart();
    const q = currentQ;
    skippedIds.current.add(q.id);
    await log(q, null, null, null, false);
    // Clear any prior answer for this question when skipping
    const newMap = { ...answerMap };
    delete newMap[q.id];
    setAnswerMap(newMap);
    const ordered = orderedAnswers(newMap);
    savePartialCtx(ordered, categorySlug, categoryName, sessionId.current, needSlug, servicePrefix, presetCity, yesNoIds);
    if (step + 1 >= allQuestions.length) finalize(ordered);
    else goToStep(step + 1, 1);
  }

  function handleBack() {
    if (step === 0) {
      // 2026-08-24 (user request): pressing back at the very first VISIBLE question,
      // when the city question was skipped because a city was already known (from the
      // shared store, not a hard /{category}/{city} URL), re-opens city selection —
      // exactly like the "🔄 עיר אחרת" chip (lib/smartIntakeStore.ts's resetCity(),
      // same function that chip calls). Safe to call unconditionally whenever
      // shouldSkipCityRef is true: on a genuine /{category}/{city} page, the parent's
      // `knownCity` derivation always prefers the URL-derived presetCity over the
      // store regardless of store state (see QuickConsultBar), so this correctly
      // becomes a no-op there — same page-scoping the chip itself already relies on.
      if (shouldSkipCityRef.current) { resetCity(); return; }
      onExitAtStart?.();
      return;
    }
    if (step - 1 === cityIdx) setCityEditing(true);
    goToStep(step - 1, -1);
  }

  function finalize(finalAnswers: Answer[]) {
    setDone(true);
    fx('quiz_completed', { cat: categorySlug, steps: allQuestions.length });
    savePartialCtx(finalAnswers, categorySlug, categoryName, sessionId.current, needSlug, servicePrefix, presetCity, yesNoIds);
    const ctx: Partial<SmartIntakeContext> = {
      categorySlug, categoryName: categoryName || categorySlug,
      cleanNeed: buildNeedFromAnswers(finalAnswers, needSlug, servicePrefix, yesNoIds),
      city: finalAnswers.find(a => a.outputKey === 'city')?.freeText || finalAnswers.find(a => a.outputKey === 'city')?.label || presetCity?.name || null,
      timingHint: finalAnswers.find(a => a.outputKey === 'urgency')?.label || null,
      priceHint: null,
      // Raw answers, in order — feeds the per-article message-template composer
      // (lib/waLinks.ts). freeText wins over the chip label when the visitor typed.
      answers: finalAnswers.map(a => ({ key: a.outputKey, label: (a.freeText || a.label || '').trim() })).filter(a => a.label),
    };
    onComplete?.(ctx);
  }

  function saveFreeText() {
    fireStart();
    saveCustomCtx(freeText, categorySlug, categoryName, sessionId.current);
    setFreeMode(false);
    setDone(true); fx('quiz_completed', { cat: categorySlug, free: 1 });
    onComplete?.({ categorySlug, categoryName: categoryName || categorySlug, customNeed: freeText.trim() || null });
  }

  // ── Free-text mode: write the request in your own words ──
  if (freeMode) {
    return (
      <div dir="rtl" style={{ padding: '16px 14px 14px' }}>
        <div className="flex items-center justify-between mb-2">
          <p style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>✏️ נסחו את הבקשה במילים שלכם</p>
          <button onClick={() => setFreeMode(false)}
            style={{ fontSize: 12.5, fontWeight: 600, color: '#0d47a1', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <span style={{ fontSize: 15, lineHeight: 1 }}>→</span> חזרה לשאלון
          </button>
        </div>
        <textarea
          autoFocus
          value={freeText}
          onChange={e => setFreeText(e.target.value)}
          rows={5}
          placeholder={`לדוגמה: ${requestExample(categorySlug, serviceWord)}`}
          style={{
            width: '100%', padding: '11px 13px', borderRadius: 12, border: '1.5px solid #cbd5e1',
            fontSize: 13.5, lineHeight: 1.65, fontFamily: 'inherit', resize: 'none', outline: 'none',
            boxSizing: 'border-box', color: '#0f172a',
          }}
        />
        <button onClick={saveFreeText} disabled={!freeText.trim()}
          style={{
            marginTop: 10, width: '100%', padding: '11px 0', borderRadius: 12, border: 'none',
            background: freeText.trim() ? '#0d47a1' : '#cbd5e1', color: '#fff',
            fontWeight: 800, fontSize: 14, cursor: freeText.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
          }}>
          הכן {multi ? 'הודעות' : 'הודעה'} מהבקשה שלי
        </button>
      </div>
    );
  }

  if (done) {
    if (condensed) {
      return (
        <div dir="rtl" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px' }}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: '#0d47a1', lineHeight: 1.4 }}>
            ✅ {multi ? 'ההודעות מוכנות — שלחו למומחים שתבחרו' : 'ההודעה מוכנה לשליחה'}
          </div>
          <button onClick={() => { setDone(false); setStep(0); setAnswerMap({}); setInlineOther(false); setOtherText(''); setAnimIn(true); }}
            style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'inherit' }}>
            <span style={{ fontSize: 12 }}>↺</span> מחדש
          </button>
        </div>
      );
    }
    return (
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '26px 16px 22px', gap: 8, minHeight: 130 }}>
        {/* Restart — top-left corner */}
        <button onClick={() => { setDone(false); setStep(0); setAnswerMap({}); setInlineOther(false); setOtherText(''); setAnimIn(true); }}
          style={{ position: 'absolute', top: 10, left: 12, fontSize: 12, fontWeight: 600, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 13 }}>↺</span> התחל מחדש
        </button>
        <div style={{ fontSize: 44 }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0d47a1', textAlign: 'center' }}>
          {multi ? 'הודעות הוואטסאפ מוכנות לשליחה' : 'הודעת הוואטסאפ מוכנה לשליחה'}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#16a34a', textAlign: 'center', lineHeight: 1.5 }}>
          שלח אותה למומחים שתבחר ותחסוך זמן וכסף 👍
        </div>
      </div>
    );
  }

  if (!currentQ) return null;

  // ── Condensed rendering (compact design's pinned scroll card) ──
  // Same handlers/state as the classic layout below — only the presentation differs:
  // one-line question + progress dots, then a single horizontally-scrollable chip row,
  // so the questionnaire stays answerable in place while pinned. Never a dead
  // "click to open" state.
  if (condensed) {
    // 2026-08-23 (user request): single-pro mode's out-of-service-area notice replaces
    // the entire condensed widget (header + chips) with a redirect message — takes over
    // BOTH condensed variants (condensedHeaderBg and plain) rather than duplicating this
    // block inside each one below.
    if (outOfAreaCity) {
      return (
        <div dir="rtl">
          <div style={{ padding: '9px 38px 8px', background: condensedHeaderBg || '#0d47a1', textAlign: 'center' }}>
            <p style={{ fontSize: 14, fontWeight: 900, color: condensedHeaderColor || '#fff', margin: 0, lineHeight: 1.35 }}>
              {/* Gender-neutral phrasing (impersonal "אין אפשרות לקבל שירות מ..." instead
                  of a gendered "X לא נותן/נותנת שירות") per
                  feedback_questionnaire_gender_neutral_phrasing.md. */}
              {singleProName
                ? `אין אפשרות לקבל שירות מ${singleProName} ב${outOfAreaCity}`
                : `אין שירות זמין ב${outOfAreaCity}`}
            </p>
          </div>
          <div style={{ padding: '10px 15px 12px', textAlign: 'center' }}>
            <p style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b', margin: 0, lineHeight: 1.4 }}>
              מעבירים אתכם למומחים שנותנים שירות ב{outOfAreaCity} בעוד רגע...
            </p>
          </div>
        </div>
      );
    }
    const isCity = currentQ.outputKey === 'city';
    const chipStyle: React.CSSProperties = {
      flexShrink: 0, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '7px 13px', borderRadius: 14,
      border: '1.5px solid #e0f2fe', background: '#f0f9ff',
      color: '#0369a1', fontWeight: 700, fontSize: 12.5,
      cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.2, transition: 'all .14s',
    };
    // 2026-09-27 (bughunter): after ‹ the answer is still in state — show it as selected instead of an unmarked row.
    const pickedValue = answerMap[currentQ.id]?.value ?? null;
    const chipFor = (value: string): React.CSSProperties => pickedValue === value ? { ...chipStyle, background: '#0369a1', color: '#fff', border: '1.5px solid #0369a1' } : chipStyle;
    const otherOpen = isCity ? cityOtherOpen : inlineOther;
    const hasNav = step > 0 || currentQ.skipAllowed;
    // City chips: the condensedHeaderBg variant lays chips in a horizontally-scrolling
    // 2-row grid, so it can afford the FULL city dataset (2026-07-29 user request —
    // sorted by population, biggest first); the plain wrapping variant stays at 12 to
    // avoid a wall of buttons.
    // CITY LAW (bughunter F1, 2026-09-27): the question object already orders the chips (known/remembered/URL city FIRST,
    // then nearby) — the renderer must consume that order, never rebuild the list from popularCities alone.
    const cityChips = isCity ? Array.from(new Set([...(currentQ.options || []).map(o => o.value), ...popularCities(condensedHeaderBg ? 120 : 12)])) : null;
    // 2026-08-23 (user request): clicking "🔍 הקלד עיר" swaps the chip grid from the
    // static population-sorted list to real-time filtered matches against the full
    // dataset as the visitor types — same 2-row/scroll engine, just a different item
    // source, so the fixed-height invariant above is untouched.
    // Founder 2026-09-22: the city search box is ALWAYS there, inside the chips row itself (chip-sized, first
    // slot) — typing filters the chips live; no toggle, no extra row, so the widget's fixed height is untouched.
    const citySearchMode = isCity && otherText.trim().length > 0;
    const citySearchResults = citySearchMode ? suggestCities(otherText, condensedHeaderBg ? 40 : 20) : [];
    const citySearchChip = isCity && (
      <div key="__citysearch" style={{ ...chipStyle, padding: '0 8px 0 10px', border: '1.5px solid #93c5fd', background: '#eff6ff', color: '#0f172a', cursor: 'text', height: 30, boxSizing: 'border-box', width: 138 }}
        onClick={e => (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus()}>
        <span style={{ fontSize: 13, flexShrink: 0 }}>🔍</span>
        <input
          value={otherText}
          onChange={e => { setOtherText(e.target.value); fireStart(); }}
          onKeyDown={e => { if (e.key === 'Enter' && otherText.trim()) handleCityAnswer(citySearchResults[0] || otherText.trim()); }}
          placeholder="חיפוש עיר..."
          aria-label="חיפוש עיר"
          style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 0, width: 88, color: '#0f172a', fontFamily: 'inherit', padding: 0 }}
        />
        {otherText.trim() && (
          <button onClick={() => handleCityAnswer(citySearchResults[0] || otherText.trim())} aria-label="בחירת העיר"
            style={{ background: '#0d47a1', color: '#fff', border: 'none', borderRadius: 8, width: 20, height: 20, cursor: 'pointer', fontSize: 11, fontWeight: 800, flexShrink: 0, padding: 0 }}>✓</button>
        )}
      </div>
    );
    const otherChip = isCity ? null : (
      <button
        onClick={() => { fireStart(); setOtherText(''); if (isCity) setCityOtherOpen(o => !o); else setInlineOther(o => !o); }}
        style={{ ...chipStyle, border: '1.5px dashed #cbd5e1', background: otherOpen ? '#eff6ff' : '#f8fafc', color: '#64748b' }}>
        {isCity ? (otherOpen ? '❌ סגור חיפוש' : '🔍 הקלד עיר') : '✏️ אחר'}
      </button>
    );
    const otherInput = !isCity && otherOpen && (
      <div style={{ margin: '6px 15px 0', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px 5px 12px', borderRadius: 14, border: '1.5px solid #93c5fd', background: '#eff6ff' }}>
        <span style={{ fontSize: 13, flexShrink: 0 }}>{isCity ? '🔍' : '✏️'}</span>
        <input
          autoFocus
          value={otherText}
          onChange={e => setOtherText(e.target.value)}
          onKeyDown={e => {
            if (e.key !== 'Enter' || !otherText.trim()) return;
            // Prefer the top live search match (real dataset city, has coords) over the
            // raw typed text whenever one exists — the raw-text fallback only fires for
            // a city genuinely missing from the local dataset.
            if (isCity) handleCityAnswer(citySearchResults[0] || otherText.trim());
            else handleAnswer(null, otherText.trim());
          }}
          placeholder={isCity ? 'הקלידו עיר...' : 'הקלד תשובה...'}
          style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, flex: 1, minWidth: 0, color: '#0f172a', fontFamily: 'inherit' }}
        />
        {otherText.trim() && (
          <button onClick={() => isCity ? handleCityAnswer(citySearchResults[0] || otherText.trim()) : handleAnswer(null, otherText.trim())}
            style={{ background: '#0d47a1', color: '#fff', border: 'none', borderRadius: 10, padding: '3px 11px', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>
            ✓
          </button>
        )}
      </div>
    );

    // ── Styled variant (QuickConsultBar's "🎯 שאלות לייעוץ מדויק" mode, condensedHeaderBg
    // set) — 2026-07-23, 3rd revision: dots AND back/skip both live inside the question's
    // own header frame (dots row, then back/skip row right under it, both on the compact
    // side) — normal document flow throughout (nothing absolutely positioned), so the
    // frame's height always contains its own content correctly regardless of whether the
    // question wraps to 1 or 3 lines. Answer chips use flex-wrap (row-major: fills row 1
    // left-to-right first, only spills into row 2 once row 1 is actually full) instead of
    // the grid-auto-flow:'column' attempt — that had stacked e.g. a 2-option question's
    // answers ONE ABOVE THE OTHER (both landing in the same column) instead of side by
    // side, which is exactly what row-major fill fixes; a fuller question (many options)
    // looks correct either way. Capped at ~2 rows tall with vertical scroll as the rare
    // overflow fallback for questions with unusually many options. The plain/unstyled
    // variant below (condensedHeaderBg omitted — every other usage: CompactIntake on
    // pro/category pages) is completely untouched.
    if (condensedHeaderBg) {
      // 2026-08-24 (user request, full restructure of this variant):
      // 1. Back/skip are now white arrow buttons pinned to the widget's physical edges
      //    (right = back, left = skip — explicit RTL layout request), not inline text
      //    buttons living beside the dots. `canGoBack` covers all three cases handleBack
      //    itself now branches on: a real earlier step, OR (step 0 with the city
      //    question skipped) reopening city selection, OR the rarely-used
      //    onExitAtStart escape hatch.
      // 2. The question is always centered across the FULL widget width (previously it
      //    only centered within its own flex-basis column, visibly off-center once the
      //    dots+buttons cluster took up space beside it), and the progress dots now sit
      //    on their own row BELOW the question instead of beside it.
      const canGoBack = step > 0 || shouldSkipCityRef.current || !!onExitAtStart;
      // 2026-08-24 (user request): bigger, and no circular background — just the arrow
      // glyph itself, sized to roughly its own footprint (was a 26px circle with a
      // 15px glyph inside; now the glyph is the whole hit target).
      const navArrowStyle: React.CSSProperties = {
        position: 'absolute', top: '50%', transform: 'translateY(-50%)', zIndex: 1,
        width: 28, height: 34, border: 'none', background: 'transparent',
        color: '#fff', fontSize: 26, fontWeight: 900,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
        padding: 0,
      };
      // ?layout=3.x (bezalel spec 01/10/26): no emoji, hairline segments / sheet rows — same handlers as every other variant
      if (barLayout === 'seg' || barLayout === 'list') {
        const HAIR = 'rgba(15,23,42,.10)';
        const list = barLayout === 'list';
        const segItems: { key: string; label: string; picked: boolean; on: () => void }[] = citySearchMode
          ? citySearchResults.map(c => ({ key: c, label: c, picked: false, on: () => handleCityAnswer(c) }))
          : cityChips
          ? cityChips.slice(0, list ? 40 : 60).map(c => ({ key: c, label: c, picked: pickedValue === c, on: () => handleCityAnswer(c) }))
          : visibleOptions(currentQ.options).map(opt => ({ key: opt.value, label: opt.label, picked: pickedValue === opt.value, on: () => handleAnswer(opt) }));
        const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: '10px 2px', margin: '-14px 0', minHeight: 44, lineHeight: '16px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }; // 36px hit area, row height unchanged
        const navRow = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 24 }}>
            {canGoBack && <button type="button" onClick={handleBack} aria-label="חזרה" style={{ ...linkBtn, color: '#64748b' }}>→ חזרה</button>}
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }} aria-label={`שאלה ${step + 1} מתוך ${totalSteps}`}>
              {list ? <>שאלה <bdi dir="ltr">{step + 1}</bdi> מתוך <bdi dir="ltr">{totalSteps}</bdi></> : <bdi dir="ltr">{step + 1}/{totalSteps}</bdi>}
            </span>
            {!list && <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{currentQ.question}</span>}
            {list && <span style={{ flex: 1 }} />}
            {cityBadge && <button ref={badgeRef} type="button" onClick={openCityChange} aria-label={`עיר: ${cityBadge} — החלפה`} style={{ ...linkBtn, color: '#1d4ed8', textDecoration: 'underline', textUnderlineOffset: 2, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cityBadge}</button>}
            {currentQ.skipAllowed && <button type="button" onClick={handleSkip} style={{ ...linkBtn, color: '#64748b' }}>דלג</button>}
          </div>
        );
        const searchBox = isCity && (
          <input value={otherText} onChange={e => { setOtherText(e.target.value); fireStart(); }}
            onKeyDown={e => { if (e.key === 'Enter' && otherText.trim()) handleCityAnswer(citySearchResults[0] || otherText.trim()); }}
            placeholder="חיפוש עיר" aria-label="חיפוש עיר"
            style={list
              ? { width: '100%', height: 44, border: `1px solid ${HAIR}`, borderRadius: 10, padding: '0 12px', fontSize: 16, fontFamily: 'inherit', color: '#0f172a', outline: 'none', marginBottom: 6, boxSizing: 'border-box' }
              : { width: 110, height: 34, border: 'none', borderInlineEnd: `1px solid ${HAIR}`, padding: '0 10px', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', background: '#fff', flexShrink: 0 }} />
        );
        const freeText = !isCity && otherOpen && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <input autoFocus value={otherText} onChange={e => setOtherText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && otherText.trim()) handleAnswer(null, otherText.trim()); }}
              placeholder="הקלידו תשובה" aria-label="תשובה חופשית" style={{ flex: 1, minWidth: 0, height: 38, border: `1px solid ${HAIR}`, borderRadius: 8, padding: '0 10px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
            <button type="button" disabled={!otherText.trim()} onClick={() => handleAnswer(null, otherText.trim())} style={{ height: 38, padding: '0 14px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', opacity: otherText.trim() ? 1 : .4 }}>אישור</button>
          </div>
        );
        const otherToggle = () => { fireStart(); setOtherText(''); setInlineOther(o => !o); };
        if (list) {
          const chev = <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0, color: '#94a3b8' }}><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
          const rowSt = (picked: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%', minHeight: 50, padding: '0 4px', border: 'none', borderBottom: `1px solid ${HAIR}`, background: picked ? '#eff4ff' : 'transparent', fontSize: 16, fontWeight: picked ? 700 : 500, color: '#0f172a', fontFamily: 'inherit', cursor: 'pointer', textAlign: 'start' });
          return (
            <div dir="rtl" data-bar-layout="list" style={{ padding: '0 16px 10px' }}>
              {navRow}
              <p style={{ margin: '4px 0 10px', fontSize: 18, fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }}>{currentQ.question}</p>
              {searchBox}
              <div ref={optionsScrollerRef} style={{ maxHeight: 'min(44vh, 360px)', overflowY: 'auto', overscrollBehavior: 'contain' }}>
                {segItems.map(it => <button key={it.key} type="button" onClick={it.on} aria-pressed={it.picked} style={rowSt(it.picked)}><span>{it.label}</span>{chev}</button>)}
                {citySearchMode && !segItems.length && <p style={{ fontSize: 13, color: '#64748b', margin: '10px 0' }}>לא נמצאה עיר — Enter ישלח את מה שהקלדת</p>}
                {!isCity && <button type="button" onClick={otherToggle} aria-expanded={otherOpen} style={{ ...rowSt(false), color: '#475569' }}><span>תשובה אחרת</span>{chev}</button>}
              </div>
              {freeText}
            </div>
          );
        }
        return (
          <div dir="rtl" data-bar-layout="seg" style={{ padding: '6px 12px 9px' }}>
            {navRow}
            <div ref={optionsScrollerRef} tabIndex={-1} style={{ marginTop: 4, overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', WebkitMaskImage: 'linear-gradient(to left, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%)', maskImage: 'linear-gradient(to left, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%)' }}>
              <div role="group" aria-label={currentQ.question} style={{ display: 'inline-flex', alignItems: 'stretch', border: `1px solid ${HAIR}`, borderRadius: 8, overflow: 'hidden', width: 'max-content', background: '#fff', marginInline: 6 }}>
                {searchBox}
                {segItems.map((it, i) => (
                  <button key={it.key} type="button" onClick={it.on} aria-pressed={it.picked}
                    style={{ height: 34, padding: '0 12px', border: 'none', borderInlineStart: i || isCity ? `1px solid ${HAIR}` : 'none', background: it.picked ? '#eff4ff' : '#fff', color: it.picked ? '#1e3a8a' : '#0f172a', fontSize: 13, fontWeight: it.picked ? 700 : 500, fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'pointer' }}>{it.label}</button>
                ))}
                {citySearchMode && !segItems.length && <span style={{ alignSelf: 'center', padding: '0 10px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>לא נמצאה עיר — Enter ישלח</span>}
                {!isCity && <button type="button" onClick={otherToggle} aria-expanded={otherOpen} style={{ height: 34, padding: '0 12px', border: 'none', borderInlineStart: `1px solid ${HAIR}`, background: otherOpen ? '#eff4ff' : '#fff', color: '#475569', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'pointer' }}>אחר</button>}
              </div>
            </div>
            {freeText}
          </div>
        );
      }
      // ?layout= preview (Founder 01/10/26): ONE row — back · question label · chips (one line, sideways scroll) · skip. Same handlers.
      if (barLayout === 'row') {
        const rowArrow: React.CSSProperties = { flexShrink: 0, width: 26, height: 40, border: 'none', background: 'transparent', color: '#1d4ed8', fontSize: 26, fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, padding: 0 };
        const rowItems = citySearchMode
          ? citySearchResults.map(c => <button key={c} onClick={() => handleCityAnswer(c)} style={chipStyle}>{c}</button>)
          : cityChips
          ? cityChips.map(c => <button key={c} onClick={() => handleCityAnswer(c)} style={chipFor(c)} aria-pressed={pickedValue === c}>{c}</button>)
          : visibleOptions(currentQ.options).map(opt => (
              <button key={opt.value} onClick={() => handleAnswer(opt)} style={chipFor(opt.value)} aria-pressed={pickedValue === opt.value}>
                {opt.emoji && <span style={{ fontSize: 13, lineHeight: 1 }}>{opt.emoji}</span>}
                {opt.label}
              </button>
            ));
        return (
          <div dir="rtl" data-bar-layout="row" aria-label={`שאלה ${step + 1} מתוך ${totalSteps}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 6px', minHeight: 58, boxSizing: 'border-box' }}>
              {canGoBack && <button onClick={handleBack} type="button" title="חזרה" aria-label="חזרה" style={rowArrow}>‹</button>}
              <p style={{ flexShrink: 0, maxWidth: '34%', margin: 0, paddingInlineStart: canGoBack ? 0 : 6, fontSize: 13, fontWeight: 900, color: '#1d4ed8', lineHeight: '16px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {isCity && '🔍 '}{currentQ.question}
              </p>
              <div ref={optionsScrollerRef} style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
                <div style={{ display: 'flex', gap: 6, height: 40, alignItems: 'center', width: 'max-content', paddingInline: 4 }}>
                  {cityBadge && (
                    <button ref={badgeRef} type="button" onClick={openCityChange} title="החלפת עיר" aria-label={`עיר: ${cityBadge} — החלפה`}
                      style={{ ...chipStyle, background: '#fff', border: '1.5px solid #93c5fd', color: '#0f172a' }}>
                      <span aria-hidden>📍</span>{cityBadge}
                    </button>
                  )}
                  {citySearchChip}
                  {rowItems}
                  {citySearchMode && !citySearchResults.length && <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>לא נמצאה עיר — Enter ישלח את מה שהקלדת</span>}
                  {otherChip}
                </div>
              </div>
              {currentQ.skipAllowed && <button onClick={handleSkip} type="button" title="דלג" aria-label="דלג" style={rowArrow}>›</button>}
            </div>
            {otherInput && <div style={{ paddingBottom: 6 }}>{otherInput}</div>}
          </div>
        );
      }
      const one = barLayout === 'one';
      return (
        // No opacity/animIn fade here (2026-07-23 user request: "no refresh effect
        // between questions") — the plain variant below still fades for its own
        // (different, arrow-slide) transition; this one just swaps content instantly.
        <div dir="rtl">
          {/* 2026-08-24 bug fix (found via a live screenshot check before shipping):
              the arrow buttons were first positioned against the OUTERMOST wrapper
              (spanning header + chips + otherInput), so `top:50%` centered them
              somewhere down in the white chip area below the header — invisible there,
              since they're styled WHITE for contrast against the blue/gradient header
              they were actually designed for. Scoped `position:relative` to just the
              header div instead, so the arrows' vertical center is the header's own
              height only, where the white color is actually legible. */}
          {/* 2026-08-24 (user follow-up, 3 rounds — this round went the OTHER direction):
              measured LIVE against the actual "הפנייה מוכנה לשליחה" (ready-to-send)
              header band elsewhere in this component (`px-3 py-2.5`, fontSize 15.5,
              font-black, single line — measures 43px total) — this bar had been
              tightened PAST that target down to 35px. Padding restored to match that
              block's own `py-2.5` (10px top/bottom) so the two states are the SAME
              height, not just "close" — question stays single-line/ellipsis-truncated
              (never 2-line-clamp) so a long question can't grow the bar past this
              matched height, per the STATIC-HEIGHT-ALWAYS rule below.
              See memory/feedback_questionnaire_engine_fixed_height.md for the standing
              rule this and the chip-grid fix below both exist to satisfy. */}
          <div style={{ position: 'relative', padding: one ? '6px 38px 5px' : '9px 38px 8px', paddingInlineEnd: cityBadge ? badgeW + (currentQ.skipAllowed ? 34 : 6) + 6 : 38, /* the badge's MEASURED width — no estimate */ background: condensedHeaderBg }}>
            {canGoBack && (
              <button onClick={handleBack} type="button" title="חזרה" aria-label="חזרה" style={{ ...navArrowStyle, right: 4 }}>‹</button>
            )}
            {currentQ.skipAllowed && (
              <button onClick={handleSkip} type="button" title="דלג" aria-label="דלג" style={{ ...navArrowStyle, left: 4 }}>›</button>
            )}
            <p ref={qTextRef} style={{ fontSize: qFont, fontWeight: 900, color: condensedHeaderColor, lineHeight: '19px', margin: 0, textAlign: 'center', height: 19, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
              {isCity && '🔍 '}{currentQ.question}
            </p>
            {cityBadge && (
              <button ref={badgeRef} type="button" onClick={openCityChange} title="החלפת עיר" aria-label={`עיר: ${cityBadge} — החלפה`}
                style={{ position: 'absolute', insetInlineEnd: currentQ.skipAllowed ? 34 : 6, top: '50%', transform: 'translateY(-50%)', height: 32, minWidth: 44, maxWidth: badgeCompact ? 92 : 150, display: 'inline-flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.35)', borderRadius: 99, padding: '0 10px', color: condensedHeaderColor, fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span aria-hidden style={{ flexShrink: 0 }}>📍</span><span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cityBadge /* bughunter 09-30: the name ALWAYS shows — compact = narrower pill + ellipsis, never a bare 📍 */}</span>
              </button>
            )}
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center', marginTop: 1 }} aria-label={`שאלה ${step + 1} מתוך ${totalSteps}`}>
              {Array.from({ length: totalSteps }).map((_, i) => ( // bughunter F2b: dots = the same stable total as the aria counter
                <span key={i} style={{
                  width: i === step ? 16 : 6, height: 6, borderRadius: 99,
                  background: i < step ? 'rgba(255,255,255,.85)' : i === step ? '#fff' : 'rgba(255,255,255,.35)',
                  transition: 'all .25s', flexShrink: 0,
                }} />
              ))}
            </div>
          </div>

          {/* HORIZONTAL-ONLY scroll, ALWAYS exactly 2 rows tall — the ONE hard
              invariant of this widget (2026-07-29 user directive, restated 2026-08-24
              after a regression let a 6-option question grow a 3rd row): the widget's
              TOTAL height must be STATIC across every step of the questionnaire,
              regardless of how many options a question has or how long they are —
              never more than 2 rows of chips, extra options scroll sideways instead.
              2026-08-24 regression + fix: an earlier pass here special-cased "ordinary
              answer options" (non-city questions) into a plain CSS flex-wrap layout for
              a cleaner 2-option look — but flex-wrap has NO row cap, so a 6-option
              question (e.g. "מה הבעיה עם המזגן?") wrapped to 3 rows and grew the whole
              bar taller, exactly the class of bug this invariant exists to prevent.
              Reverted to ALWAYS using this same rigid engine for both city chips and
              ordinary options — one code path, no special-casing that can regress
              independently again. Two bugs previously fixed here, both traced to the
              old grid-auto-flow:'row' + fixed 6-column template: (1) "second row empty/
              wasted" — a fixed 6-column template + row-major flow left row 2 completely
              unused for any question with <=6 options. (2) CRITICAL — the city question
              (up to 120 chips) overflowed the 12-slot 6x2 grid into unbounded implicit
              ROWS (grid's row-major algorithm adds ROWS on overflow, not columns) — the
              exact "widget grows to fill the screen" failure this whole rewrite exists
              to prevent. FIX: grid-auto-flow:'column' with grid-template-ROWS fixed at
              exactly 2 (columns implicit) packs items top-to-bottom then left-to-right —
              rows can structurally never exceed the 2 explicit ones, for any item count. */}
          <div ref={optionsScrollerRef} style={{ overflowX: 'auto', overflowY: 'hidden', padding: one ? '7px 15px 7px' : '7px 15px 3px', WebkitOverflowScrolling: 'touch', display: 'flex', justifyContent: 'safe center' }}>
            {(() => {
              const items = citySearchMode
                ? citySearchResults.map(c => (
                    <button key={c} onClick={() => handleCityAnswer(c)} style={chipStyle}>{c}</button>
                  ))
                : cityChips
                ? cityChips.map(c => (
                    <button key={c} onClick={() => handleCityAnswer(c)} style={chipFor(c)} aria-pressed={pickedValue === c}>{c}</button>
                  ))
                : visibleOptions(currentQ.options).map(opt => (
                    <button key={opt.value} onClick={() => handleAnswer(opt)} style={chipFor(opt.value)} aria-pressed={pickedValue === opt.value}>
                      {opt.emoji && <span style={{ fontSize: 13, lineHeight: 1 }}>{opt.emoji}</span>}
                      {opt.label}
                    </button>
                  ));
              // 2026-08-24 bug fix (user report, reproduced on the city-chip list, but
              // this interleaving applies equally to ordinary answer options now):
              // splitting into a first-half / second-half (items[0..mid), items[mid..))
              // put the item at rank `mid` directly under item 0 — since both rows are
              // left-aligned within the same shared-width column, the first-seen
              // "column" paired rank 1 with rank ~mid, making the overall reading order
              // (top row then bottom row, both visible together) look essentially
              // random instead of the intended order. Interleaving (row1 = even ranks,
              // row2 = odd ranks) makes column-by-column, top-then-bottom reading order
              // EXACTLY equal to the original order — column 0 shows items 1&2, column 1
              // shows items 3&4, etc. — matching what a single row would read like, just
              // wrapped into 2 lines.
              const rows: React.JSX.Element[][] = one ? [[]] : [[], []]; // 'one' preview layout: a single sideways-scrolling row
              // bughunter 01/10/26 (graded scales "עד 3 · מעל 6 / 3-6"): a short answer list (≤6, no sideways scroll) is read ROW by row,
              // so it fills row 1 first, then row 2, keeping the order (עד 3 · 3-6 / מעל 6). City chips keep the interleave above.
              const rowMajor = !citySearchMode && !cityChips && items.length <= 6;
              items.forEach((item, i) => rows[one ? 0 : rowMajor ? (i < Math.ceil(items.length / 2) ? 0 : 1) : i % 2].push(item));
              if (citySearchChip) rows[0].unshift(citySearchChip as React.JSX.Element);
              if (citySearchMode && !citySearchResults.length) rows[rows.length - 1].push(<span key="__nomatch" style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>לא נמצאה עיר — Enter ישלח את מה שהקלדת</span>);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 'max-content' }}>
                  {rows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, height: 30, alignItems: 'center' }}>
                      {row}
                      {i === rows.length - 1 && otherChip}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {otherInput}
        </div>
      );
    }

    return (
      <div dir="rtl">
        <div style={{ transition: 'opacity .18s ease', opacity: animIn ? 1 : 0 }}>
          {/* Question + progress dots on one row — now that the condensed bar replaces the
              site header (CompactIntake) instead of sharing space under it, there's enough
              vertical room for the question to wrap 2-3 lines instead of being squeezed. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '13px 15px 0' }}>
            <p style={{ fontSize: 14, fontWeight: 900, color: '#0f172a', lineHeight: 1.4, margin: 0, minWidth: 0 }}>
              {isCity && '🔍 '}{currentQ.question}
              {cityBadge && (
              <button type="button" onClick={openCityChange} aria-label={`עיר: ${cityBadge} — החלפה`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, margin: '0 0 0 0', padding: '4px 10px', borderRadius: 99, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#0d47a1', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', verticalAlign: 'middle', marginInlineStart: 6, minHeight: 32, minWidth: 44, justifyContent: 'center' }}>📍 {cityBadge}</button>
            )}
            </p>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }} aria-label={`שאלה ${step + 1} מתוך ${totalSteps}`}>
              {Array.from({ length: totalSteps }).map((_, i) => ( // bughunter F2b: dots = the same stable total as the aria counter
                <span key={i} style={{
                  width: i === step ? 16 : 6, height: 6, borderRadius: 99,
                  background: i < step ? '#16a34a' : i === step ? '#0d47a1' : '#e2e8f0',
                  transition: 'all .25s',
                }} />
              ))}
            </div>
          </div>

          {/* Answer chips — one row, horizontally scrollable, tap to answer in place */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '9px 15px 3px', WebkitOverflowScrolling: 'touch' }}>
            {citySearchChip}
            {citySearchMode ? citySearchResults.map(c => (
              <button key={c} onClick={() => handleCityAnswer(c)} style={chipStyle}>{c}</button>
            )) : cityChips ? cityChips.map(c => (
              <button key={c} onClick={() => handleCityAnswer(c)} style={chipFor(c)} aria-pressed={pickedValue === c}>{c}</button>
            )) : visibleOptions(currentQ.options).map(opt => (
              <button key={opt.value} onClick={() => handleAnswer(opt)} style={chipFor(opt.value)} aria-pressed={pickedValue === opt.value}>
                {opt.emoji && <span style={{ fontSize: 13, lineHeight: 1 }}>{opt.emoji}</span>}
                {opt.label}
              </button>
            ))}
            {otherChip}
          </div>

          {otherInput}

          {/* Mini footer: back / skip */}
          {hasNav && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '6px 15px 10px' }}>
              {step > 0 ? (
                <button onClick={handleBack}
                  style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 13, lineHeight: 1 }}>→</span> חזרה
                </button>
              ) : <span />}
              {currentQ.skipAllowed ? (
                <button onClick={handleSkip}
                  style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  דלג <span style={{ fontSize: 13, lineHeight: 1 }}>←</span>
                </button>
              ) : <span />}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" style={{ padding: '16px 14px 12px' }}>
      {/* Question + chips */}
      <div style={{ transition: 'opacity .18s ease, transform .18s ease', opacity: animIn ? 1 : 0, transform: animIn ? 'translateX(0)' : `translateX(${animDir === 1 ? -12 : 12}px)` }}>
        {/* Question title + a left-aligned pencil action */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
          <p style={{ fontSize: 15.5, fontWeight: 900, color: '#0f172a', lineHeight: 1.45, margin: 0 }}>
            {currentQ.question}
            {cityBadge && (
              <button type="button" onClick={openCityChange} aria-label={`עיר: ${cityBadge} — החלפה`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, margin: '0 0 0 0', padding: '4px 10px', borderRadius: 99, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#0d47a1', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', verticalAlign: 'middle', marginInlineStart: 6, minHeight: 32, minWidth: 44, justifyContent: 'center' }}>📍 {cityBadge}</button>
            )}
          </p>
          {currentQ.outputKey === 'city' ? (
            null /* city search now lives inside CityQuestion's first grid cell (Founder 2026-09-22) */
          ) : (
            <button onClick={() => { setFreeText(''); setFreeMode(true); }}
              style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0, minHeight: 44 }}>
              <span style={{ fontSize: 14 }}>✏️</span> נסח פנייה ללא שאלון
            </button>
          )}
        </div>

        {currentQ.outputKey === 'city' ? (
          <CityQuestion onSelect={handleCityAnswer} otherOpen={cityOtherOpen} />
        ) : (
        /* Chip-width-to-content engine (2026-08-22 user request): was a CSS Grid with
            `minmax(108px, 1fr)` columns — every chip stretched to fill an equal 1fr
            share of the row regardless of its own text length, so a short label like
            "מדבך"/"שירותים" rendered as a padded-out pill the same width as a much
            longer neighbor, wasting horizontal room that could've shown another chip.
            flex-wrap has no shared column track — each chip is exactly as wide as its
            own content (+ padding), and wraps naturally, so short answers pack tighter
            and more options are visible per row without scrolling. */
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {visibleOptions(currentQ.options).map(opt => (
            <button key={opt.value} onClick={() => handleAnswer(opt)}
              style={{
                display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
                flexShrink: 0, minHeight: 44, padding: '7px 14px', borderRadius: 16,
                border: '1.5px solid #e0f2fe', background: '#f0f9ff',
                color: '#0369a1', fontWeight: 700, fontSize: 12,
                cursor: 'pointer', transition: 'all .14s', fontFamily: 'inherit',
                textAlign: 'center', lineHeight: 1.1, whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => Object.assign((e.currentTarget as HTMLElement).style, { background: '#0d47a1', color: '#fff', borderColor: '#0d47a1' })}
              onMouseLeave={e => Object.assign((e.currentTarget as HTMLElement).style, { background: '#f0f9ff', color: '#0369a1', borderColor: '#e0f2fe' })}
            >
              {opt.emoji && <span style={{ fontSize: 15, lineHeight: 1 }}>{opt.emoji}</span>}
              <span>{opt.label}</span>
            </button>
          ))}

          {/* "אחר" — ALWAYS available; expands to a full-row input */}
          {!inlineOther ? (
            <button onClick={() => { fireStart(); setInlineOther(true); }}
              style={{
                display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
                flexShrink: 0, minHeight: 44, padding: '7px 14px', borderRadius: 16,
                border: '1.5px dashed #cbd5e1', background: '#f8fafc',
                color: '#64748b', fontWeight: 600, fontSize: 12,
                cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.1, whiteSpace: 'nowrap',
              }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>✏️</span>
              <span>אחר</span>
            </button>
          ) : (
            <div style={{
              flexBasis: '100%',
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 8px 6px 14px', borderRadius: 16,
              border: '1.5px solid #93c5fd', background: '#eff6ff',
            }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>✏️</span>
              <input
                autoFocus
                value={otherText}
                onChange={e => setOtherText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && otherText.trim() && handleAnswer(null, otherText.trim())}
                placeholder="הקלד תשובה..."
                style={{
                  border: 'none', background: 'transparent', outline: 'none',
                  fontSize: 13.5, flex: 1, minWidth: 0, color: '#0f172a', fontFamily: 'inherit',
                }}
              />
              {otherText.trim() && (
                <button onClick={() => handleAnswer(null, otherText.trim())}
                  style={{ background: '#0d47a1', color: '#fff', border: 'none', borderRadius: 12, padding: '4px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  ✓
                </button>
              )}
            </div>
          )}
        </div>
        )}

      </div>

      {/* Footer: back (right) + skip (left) — only rendered when there's an action */}
      {(step > 0 || currentQ.skipAllowed) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
          {step > 0 ? (
            <button onClick={handleBack} style={navPill('#475569')}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#f1f5f9'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = '#fff'}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>→</span> חזרה
            </button>
          ) : <span />}

          {currentQ.skipAllowed ? (
            <button onClick={handleSkip} style={navPill('#64748b')}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#f1f5f9'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = '#fff'}>
              דלג <span style={{ fontSize: 16, lineHeight: 1 }}>←</span>
            </button>
          ) : <span />}
        </div>
      )}

      {/* Elegant progress slider — compact, at the very bottom */}
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 99, background: '#eef2f7', overflow: 'hidden', position: 'relative' }}>
          <div style={{
            position: 'absolute', inset: 0, width: `${((step + 1) / allQuestions.length) * 100}%`,
            borderRadius: 99, background: 'linear-gradient(90deg,#0d47a1,#1976d2,#42a5f5)',
            transition: 'width .45s cubic-bezier(.4,0,.2,1)',
            boxShadow: '0 0 8px rgba(13,71,161,.4)',
          }} />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', flexShrink: 0 }}>
          {step + 1}/{allQuestions.length}
        </span>
      </div>
    </div>
  );
}

function navPill(color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    minHeight: 44, padding: '7px 15px', borderRadius: 18,
    border: '1.5px solid #e2e8f0', background: '#fff',
    color, fontWeight: 700, fontSize: 13.5,
    cursor: 'pointer', fontFamily: 'inherit', transition: 'background .14s',
  };
}
